// ============================================================
// TILT — PLAYFIELD 3D  (the renderer, with a third dimension)
// ------------------------------------------------------------
// A DROP-IN REPLACEMENT FOR Playfield.js. Same props, same
// callbacks, same rules. The engine does not know this file
// exists, and that is the entire design.
//
//     import Playfield from "../Playfield3D";   // ← the whole swap
//
// ── THE ONE BIG IDEA ─────────────────────────────────────────
// We are not rebuilding the game. We are rebuilding the RENDERER.
// TiltEngine, World, TableKit, Powers, the HUD, the menus, the
// duel bus — none of them change. Not one line. This file READS
// the same snapshot Playfield.js read and draws it with meshes
// instead of a 2D context.
//
// ── THE RULE (inherited from Playfield.js, and stricter here) ─
// This file READS the engine. It NEVER writes to it. It never
// mutates a ball, never nudges a collider, never adds a field to
// a table. If a renderer can perturb physics, the sim stops being
// deterministic and PvP dies. So it can't.
//
// ── THE THIRD DIMENSION IS A FUNCTION, NOT A LIE ─────────────
//
//   The solver is 2D. It always will be. A ball has (x, y) and
//   nothing else, and every table ever written is authored in that
//   plane. So where does HEIGHT come from, and how do we add it
//   without the picture lying about the physics — the exact bug
//   Playfield.js's MOVING_TAGS comment rails against?
//
//   THE ANSWER: height is a pure function of position.
//
//       h = heightAt(x, y)
//
//   ONE height field, derived from the table's own geometry, and
//   EVERYTHING samples it — the deck, the walls, the posts, the
//   flippers, and the ball. A ramp's bed rises, and the ball
//   rising with it is not an animation; it is the same lookup.
//   Nothing can disagree, because there is only one answer.
//
//   The physics still owns (x, y). The renderer owns (y-up), and
//   it owns it as a FUNCTION of what physics already decided. The
//   picture cannot lie, because the picture has no opinion.
//
//   This is what buys the loops their banking, the ramps their
//   climb, the holes their depth, and the whole table its stories.
//
// ── AND IT IS ALL DERIVED. NOTHING IS AUTHORED. ──────────────
//   Not one field was added to a table. The height field is read
//   out of `world.colliders` — the rides are found by their id
//   prefixes (`ramp_L_railA`, `loop_in`, `spiral_i12`), the wells
//   by their sensor tags. Add a loop to any table tomorrow and it
//   gets an elevated, banked, walled ring for free, and this file
//   never gets touched.
//
// ── Y-ELEVATION TABLE (keep this current or you WILL z-fight) ─
//    -70   cabinet floor
//    -26   bottom of a well (hole / chute / wormhole)
//      0   deck datum (the plane's zero; wells go under, rides over)
//    +h    deck surface at (x,y)  ← heightAt(). Everything rides it.
//    +0.7  flat lamp pucks (targets, nodes, prize slots)
//    +h+r  ball centre
//    +h+H  wall tops (see WALL_H)
//
// ── PF PALETTE ONLY ──────────────────────────────────────────
// Everything in here uses PF.*. Never UI.*. This is inside the
// glass. (theme.js, LAW 1.) The camera buttons are the one
// exception — they are chrome, they sit OUTSIDE the canvas, and
// they are the only DOM in this file.
//
// ── INSTALL ──────────────────────────────────────────────────
//    npm install three @react-three/fiber @react-three/drei
// Three packages. No .glb, no Blender, no asset pipeline. Every
// texture in here is drawn at runtime on a <canvas>.
// ============================================================

import { useRef, useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { PF, tintOf } from "./theme";
import SetPieces, { BOSS_TOP, useHave, WALL_VARIANT } from "./SetPieces3D";   // the Blender layer (2026-10-25)

// ══════════════════════════════════════════════════════════════
//  LAYER 1 — FOUNDATION
// ══════════════════════════════════════════════════════════════
// World units ARE table units. A 400×700 table is 400×700 units
// wide. No UNIT constant, no scale factor, no conversion bugs at
// 2am. The only mapping is centring:
//
//     world.x = table.x - W/2      world.z = table.y - H/2
//
// so the table straddles the origin and the camera math is clean.
// +Z is toward the player (the drain). -Z is the top arch.

const CELL       = 4;     // height-field resolution, table units
const RIDE_PAD   = 13;    // how far an unpaired ride's bed spreads
const APRON      = 34;    // a mound's run-out. Long = a HILL you drive up.
const RAIL_APRON = 8;     // a ramp's run-out. Short — and hidden under the rail.
const WELL_APRON = 12;    // a hole’s run-out. A hole is SUPPOSED to be steep.
const EXIT_RUN   = 52;    // a ramp’s LANDING — the deck has to come back down
const CHUNK      = 20;    // a wall is cut into chunks this long, so it can
                          // FOLLOW the terrain instead of floating over it
const SKIRT      = 55;    // every wall is buried this deep. Nothing floats.
const BALL_R     = 7;     // the ball's radius. A wall's true footprint is
                          // thickness/2 + this — that's the band of deck the
                          // ball's CENTRE can never enter, and therefore the
                          // only band where the deck is allowed to fall off a
                          // cliff. See the ramp bed, below.

// ── HEIGHT BANDS ──────────────────────────────────────────────
// Colour is a label. HEIGHT is a place. This is the table that
// makes the Midway read as a midway from any angle: the coaster
// TOWERS, the fork sits low, and you feel which ride you're on
// without reading a word.
const RIDE_H = {
  ramp:    30,   // the helter-skelter — CLIMBS along its length
  orbit:   22,
  loop:    22,   // the big top — an elevated, banked ring on a mound
  spiral:  24,   // and it DESCENDS as it coils (see spiralIndex)
  coaster: 32,   // the tallest thing on any table
  fork:    10,
  fork_L:  10,
  fork_R:  10,
};

// ── WELLS ─────────────────────────────────────────────────────
// A hole is an ABSENCE. In 2D that had to be faked with a black
// gradient. In 3D it can just BE one: the deck goes down.
const WELL_D = {
  hole:     -26,
  chute:    -26,
  wormhole: -26,
  scoop:    -13,
  pool:     -17,   // the whirlpool — a funnel, and it PULLS
};

// ── WALL HEIGHTS ──────────────────────────────────────────────
// How tall each thing stands ABOVE the deck it's standing on.
const WALL_H = {
  wall:        16,
  stage:        6,   // Willie's stage (Cirque W3): a low plinth, so the code-drawn stand-in hides inside the Blender stage
  rail:        14,
  ramp:        18,
  orbit:       18,
  loop:        18,
  spinner:     12,
  post:        19,
  bumper:      23,
  slingshot:   17,
  ghostwall:   19,
  captive:     10,
  turnstile:    9,
  cart:         8,
  flipper:      9,
  crucible:    14,
  spire_top:   30,
  vent:        10,
  midway:      14,
};
const wallH = (tag) => WALL_H[tag] ?? 16;   // ← every lookup gets a fallback

// ── TAG → COLOUR ──────────────────────────────────────────────
// The exact map from Playfield.js. The visual language does not
// change just because the geometry did.
const TAG_COLOR = {
  wall: PF.wall, post: PF.rail, bumper: PF.neon, slingshot: PF.ember,
  target: PF.gold, grudge: PF.ember, ramp: PF.rail, orbit: PF.rail,
  spinner: PF.violet, drain: PF.emberDim, node: PF.toxic,
  ghostwall: PF.violet, crucible: PF.gold, spire_top: PF.gold,
  vent: PF.toxic, anchor: PF.gold, anchor_hostile: PF.ember,
  flipper: PF.flipper,
  loop: PF.violet, spiral: PF.violet, scoop: PF.gold, hole: PF.ember,
  pool: PF.violet, captive: PF.bone, turnstile: PF.toxic, cart: PF.gold,
  chute: PF.ice, fork: PF.neon, fork_L: PF.neon, fork_R: PF.neon,
  wormhole: PF.violet, coaster: PF.rail, midway: PF.gold, ticket: PF.gold,
  prize_0: PF.gold, prize_1: PF.neon, prize_2: PF.ember,
  prize_3: PF.toxic, prize_4: PF.violet, prize_5: PF.ice,
};
const colorOf = (tag) => TAG_COLOR[tag] || PF.wall;

// ══════════════════════════════════════════════════════════════
//  SKINS — a table's COSTUME. Render-layer only, like `decor`.
// ══════════════════════════════════════════════════════════════
// A skin is one string on the table (`skin: "cirque"`) and this
// renderer does the rest: it retints the STRUCTURAL tags (walls,
// rails, posts — the anonymous steel), swaps the marquee art, the
// deck bake and the bumper bodies, and hangs decoration (bunting,
// canopies, spotlights) that the physics never hears about.
//
// ⚠ SEMANTIC colours are NOT skinnable. Gold = value, ember =
//   danger, toxic = objective, violet = arcane, ice = freeze.
//   That vocabulary is the same on every table in the game — a
//   skin that repaints a target repaints the LANGUAGE, and the
//   player pays for it on the next table. Structure only.
const SKIN_TINT = {
  cirque: {
    wall:      "#4b2745",   // painted big-top plum, not bare steel
    post:      "#f3e3c3",   // cream tent-pole
    rail:      "#caa45e",   // worn brass
    ramp:      "#caa45e",
    orbit:     "#caa45e",
    loop:      "#d94757",   // the big top itself is crimson
    slingshot: "#d94757",
    bumper:    "#d94757",
    coaster:   "#caa45e",
    midway:    "#f2c14e",
  },
};
const skinColorOf = (skin, tag) =>
  (skin && SKIN_TINT[skin] && SKIN_TINT[skin][tag]) || colorOf(tag);

// The circus's own paintbox — used by the dressing layer below.
const CIRQUE = {
  red:   "#d94757",
  cream: "#f3e3c3",
  brass: "#f2c14e",
  bulb:  "#ffd9a0",
  plum:  "#2c1530",
};

// ── Striped canvas — the single texture that sells the tent ───
// Cached by colour pair; wraps around cones and drums. Runtime
// canvas, same as every other texture in this file. No assets.
const _stripeCache = new Map();
function stripeTexture(a, b, n = 12) {
  const key = `${a}|${b}|${n}`;
  if (_stripeCache.has(key)) return _stripeCache.get(key);
  const c = document.createElement("canvas");
  c.width = 256; c.height = 64;
  const g = c.getContext("2d");
  const w = 256 / n;
  for (let i = 0; i < n; i++) {
    g.fillStyle = i % 2 ? a : b;
    g.fillRect(i * w, 0, w + 1, 64);
  }
  // A soft top-down shade so the stripe reads as CLOTH, not flag.
  const sh = g.createLinearGradient(0, 0, 0, 64);
  sh.addColorStop(0, "rgba(255,255,255,0.10)");
  sh.addColorStop(1, "rgba(0,0,0,0.22)");
  g.fillStyle = sh;
  g.fillRect(0, 0, 256, 64);
  const t = new THREE.CanvasTexture(c);
  if (THREE.SRGBColorSpace) t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = THREE.RepeatWrapping;
  _stripeCache.set(key, t);
  return t;
}

// Tags that light up. These get their own mesh so their emissive
// can be driven by table.lamps() every frame.
const LAMP_TAGS = new Set([
  "target", "node", "grudge", "crucible", "ticket", "spinner",
  "prize_0", "prize_1", "prize_2", "prize_3", "prize_4", "prize_5",
]);

// Sensor tags that get bespoke 3D bodies (a hole must look like a
// hole, not like a dashed ring — same law as Playfield.js).
const DEVICE_TAGS = new Set(["scoop", "hole", "pool", "chute", "wormhole"]);

// Colliders whose endpoints are rewritten every tick by a table's
// onTick. They are NOT baked. They are drawn where they ACTUALLY
// ARE, every frame, or the picture lies about the physics.
const MOVING_TAGS = new Set(["turnstile", "cart"]);

// Which sensor tags mark a RIDE (an elevated bed).
const RIDE_TAGS = new Set(Object.keys(RIDE_H));

// ── mulberry32 — deterministic variety, never Math.random() ────
// Copied verbatim into every IE 3D game. Math.random() in a render
// path means the world reshuffles on every re-render, and in a
// DUEL it means two players see two different tables. Seeded from
// the object's own id means chunk 47 is the same chunk forever, on
// every client, for every player.
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const hashStr = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
};
const rndOf = (id) => mulberry32(hashStr(String(id)) % 2147483647);

const smooth = (e0, e1, x) => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0 || 1)));
  return t * t * (3 - 2 * t);
};

// ══════════════════════════════════════════════════════════════
//  THE HEIGHT FIELD — where the third dimension comes from
// ══════════════════════════════════════════════════════════════
//
// ⚠ EVERY SUFFIX TableKit CAN EMIT MUST BE LISTED HERE.
//
//   TableKit's devices name their colliders `${id}_railA`,
//   `${id}_in`, `${id}_i12`, `${id}_score`… That naming is the
//   ONLY thing tying a ramp's two rails to the ramp's scoring
//   sensor — and the sensor is the only collider that carries the
//   tag `"ramp"` (the rails are plain `"wall"`). So the id suffix
//   is how a ride knows it's a ride.
//
//   This is the same allowlist trap as Seg()'s: add a device to
//   TableKit with a new suffix, forget to add it here, and its
//   pieces silently detach from their ride and sit on the floor
//   while the bed rises out from under them. No error. Nothing to
//   grep for. ADD THE SUFFIX.
const SUFFIX = /_(railA|railB|score|round|in|out|outL|outR|split|hub|pull|eye|rim|hold|cup|exit|arm\d+|[ios]\d+|[LR])$/;
const baseId = (id) => String(id ?? "").replace(SUFFIX, "");

// A spiral DESCENDS. Its segments are numbered (`spiral_i0` …
// `spiral_i29`), so the coil can fall away as it goes — for free,
// from a name.
const spiralIndex = (id) => {
  const m = /_[io](\d+)$/.exec(String(id ?? ""));
  return m ? parseInt(m[1], 10) : null;
};

// Two segs are a RAIL PAIR if they're parallel and the same length —
// which is exactly and only what `ramp()` emits. A fork's two walls
// DIVERGE (that's what makes it a wishbone), so this correctly
// refuses to pair them, and the fork falls through to the generic
// path. The test is the geometry, not the name.
function railPair(segs) {
  if (!segs || segs.length !== 2) return null;
  const [A, B] = segs;
  const la = Math.hypot(A.b.x - A.a.x, A.b.y - A.a.y) || 1;
  const lb = Math.hypot(B.b.x - B.a.x, B.b.y - B.a.y) || 1;
  if (Math.abs(la - lb) / Math.max(la, lb) > 0.2) return null;   // same length?
  const ux = (A.b.x - A.a.x) / la, uy = (A.b.y - A.a.y) / la;
  const vx = (B.b.x - B.a.x) / lb, vy = (B.b.y - B.a.y) / lb;
  if (Math.abs(ux * vy - uy * vx) > 0.18) return null;           // parallel?
  return [A, B];
}

function buildHeightfield(colliders, W, H) {
  const nx = Math.ceil(W / CELL) + 1;
  const nz = Math.ceil(H / CELL) + 1;
  const grid = new Float32Array(nx * nz);   // starts flat. It IS a table.

  // ── THE STAMP ───────────────────────────────────────────────
  //
  // ⚠ A RUN-OUT FALLS TO THE GROUND UNDER IT — NOT TO ZERO.
  //
  //   The first version faded every stamp toward 0, which is only
  //   correct on a flat table. Put the Foundry's whirlpool (a -26
  //   well) inside the Big Top's raised bed (+22) and the two
  //   stamps fight: the well wins in its own radius, the bed wins
  //   one cell later, and the deck becomes a 48-unit SHEER SHAFT
  //   with a 342% grade — a hole punched clean through the side of
  //   a hill. The proof loop found it in 60ms. Eyes would not have.
  //
  //   So a stamp BLENDS with whatever is already there. A hole dug
  //   in a hill has its floor 26 units below THE HILL, and its lip
  //   meets the hill exactly. Terrain composes. It doesn't fight.
  const stamp = (ax, ay, bx, by, r, apron, h0, h1, dig) => {
    const R = r + apron;
    const minX = Math.max(0, Math.floor((Math.min(ax, bx) - R) / CELL));
    const maxX = Math.min(nx - 1, Math.ceil((Math.max(ax, bx) + R) / CELL));
    const minZ = Math.max(0, Math.floor((Math.min(ay, by) - R) / CELL));
    const maxZ = Math.min(nz - 1, Math.ceil((Math.max(ay, by) + R) / CELL));
    const dx = bx - ax, dy = by - ay;
    const L2 = dx * dx + dy * dy || 1;
    for (let iz = minZ; iz <= maxZ; iz++) {
      for (let ix = minX; ix <= maxX; ix++) {
        const px = ix * CELL, py = iz * CELL;
        let t = ((px - ax) * dx + (py - ay) * dy) / L2;
        t = Math.min(1, Math.max(0, t));
        const d = Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
        if (d > R) continue;
        const fall = 1 - smooth(r, R, d);          // the run-out
        const i = iz * nx + ix;
        const cur = grid[i];
        const target = h0 + (h1 - h0) * t;
        const v = cur + (target - cur) * fall;     // blend, don't overwrite
        grid[i] = dig ? Math.min(cur, v) : Math.max(cur, v);
      }
    }
  };
  const disc = (cx, cy, r, apron, h, dig) => stamp(cx, cy, cx, cy, r, apron, h, h, dig);

  // ⚠ A RUN-OUT IS A SMOOTHSTEP, AND A SMOOTHSTEP IS STEEPER THAN IT LOOKS.
  //
  //   A 30-unit hill with a 34-unit apron sounds like an 88% grade, and
  //   it isn't: smoothstep's *mid-slope* is 1.5× its average, so the
  //   middle of that run-out is at 132% and the ball rolls up nothing
  //   but the middle. The proof loop read 124% off the Foundry's Big
  //   Top and 126% off Spite's arch — both hills that "should" have been
  //   fine on paper. So the apron is not a number anyone picks. It is
  //   derived from the drop it has to absorb.
  //
  //   And then it has to survive its NEIGHBOURS. Spite's Big Wheel and
  //   its Spire land their skirts on the same patch of deck. Alone,
  //   each is a 75% grade. max()'d together, one mound's plateau meets
  //   the other's mid-fall and the seam between them reads 125% — a
  //   kink steeper than either hill that made it. Nothing is wrong
  //   with either mound. The margin just has to be big enough that two
  //   of them stacked are still a hill. 2.4 puts a lone mound at 62%
  //   and the worst seam on any of the three tables at 88%.
  const gradeApron = (h) => Math.max(APRON, Math.abs(h) * 2.4);

  const sampleRaw = (x, y) => {
    const ix = Math.min(nx - 1, Math.max(0, Math.round(x / CELL)));
    const iz = Math.min(nz - 1, Math.max(0, Math.round(y / CELL)));
    return grid[iz * nx + ix];
  };

  // ── PASS 1 — find the rides ────────────────────────────────
  // A ride is a group of colliders sharing an id prefix, at least
  // one of which carries a ride tag. Nothing is authored; the
  // renderer DISCOVERS each ride from names the table already had
  // to give it.
  const rides = new Map();   // prefix → { tag, maxIdx, solids, segs }
  for (const c of colliders) {
    if (!RIDE_TAGS.has(c.tag)) continue;
    const key = baseId(c.id);
    if (!key) continue;
    if (!rides.has(key)) rides.set(key, { tag: c.tag, maxIdx: 0, solids: 0, segs: [] });
  }
  for (const c of colliders) {
    const r = rides.get(baseId(c.id));
    if (!r) continue;
    if (!c.sensor) { r.solids++; if (c.kind === "seg") r.segs.push(c); }
    const i = spiralIndex(c.id);
    if (i != null && i > r.maxIdx) r.maxIdx = i;
  }
  // ⚠ A LONE SENSOR IS NOT A RIDE. `SensorSeg(60,190,60,226,{tag:"orbit",
  //   id:"orbit_exit"})` is a rollover that SCORES an orbit — it is not
  //   an orbit. Without this guard it raises a 22-unit mound in the
  //   middle of open deck with nothing standing on it, and the ball
  //   rolls up a hill that exists for no reason. A ride is a bed with
  //   WALLS on it. No walls, no bed.
  for (const [k, r] of rides) if (r.solids === 0) rides.delete(k);

  // ── PASS 2 — raise the beds ────────────────────────────────
  const done = new Set();

  for (const [key, ride] of rides) {
    const H0 = RIDE_H[ride.tag] ?? 20;

    // ⚠ A RAMP IS TWO RAILS, AND THE BED GOES BETWEEN THEM.
    //
    //   Stamp a bed around EACH rail and the bed has to be at least
    //   half the channel wide just to meet in the middle — which
    //   means it also spills that far OUTSIDE, so the ball standing
    //   next to a ramp is standing on top of it. The fix isn't a
    //   fudge factor; it's noticing that ramp() always emits a
    //   parallel PAIR, and that a pair has a MIDLINE. Stamp the
    //   midline, width = the gap, and the bed's edge lands under
    //   the rails — where a 16-unit wall, buried 55 units deep,
    //   hides the drop completely. The ball outside the ramp stands
    //   on flat deck. The ball inside rides the ramp. Nobody floats.
    const pair = railPair(ride.segs);
    if (pair) {
      const [A, B] = pair;
      const th = (A.thickness || 4) / 2;
      const m0 = { x: (A.a.x + B.a.x) / 2, y: (A.a.y + B.a.y) / 2 };   // the mouth
      const m1 = { x: (A.b.x + B.b.x) / 2, y: (A.b.y + B.b.y) / 2 };   // the top
      const halfW = Math.hypot(
        (A.a.x + A.b.x) / 2 - (B.a.x + B.b.x) / 2,
        (A.a.y + A.b.y) / 2 - (B.a.y + B.b.y) / 2
      ) / 2;
      const R = halfW + th + 2;
      const climb = ride.tag === "ramp";

      // ⚠ "UNDER THE RAIL" IS AN ARITHMETIC CLAIM, AND IT HAS TO CHECK OUT.
      //
      //   The first cut stamped a bed of radius halfW+th+2 and let it
      //   run out over 8 units — so the drop FINISHED at halfW+th+10.
      //   But a rail only blocks the ball out to halfW+th+BALL_R, i.e.
      //   halfW+th+7. Three units of a 30-unit drop therefore landed on
      //   deck the ball can stand on: a 189% grade on the Foundry's
      //   Helter-Skelter, a 172% one on Spite's Spire. A ledge running
      //   the whole length of both ramps, one ball-width wide, invisible
      //   in any screenshot, and the ball would have climbed it.
      //
      //   So don't pick the run-out and hope. DERIVE it. The drop must
      //   begin and end inside the band the ball's centre cannot enter:
      //
      //       inner reachable edge  =  halfW − (th + BALL_R)
      //       outer reachable edge  =  halfW + (th + BALL_R)
      //
      //   Plateau to the inner edge, land flat on the outer one, and the
      //   entire cliff lives under sixteen units of steel. The ball on
      //   the ramp rides a flat bed; the ball beside it stands on flat
      //   deck; there is nowhere to put a ball that is on a slope.
      const hide = th + BALL_R;
      const rIn  = Math.max(2, halfW - hide);
      const rail = (halfW + hide) - rIn;   // the run-out, to the exact unit

      stamp(m0.x, m0.y, m1.x, m1.y, rIn, rail,
        climb ? H0 * 0.08 : H0,    // the mouth is nearly deck-level…
        H0,                        // …and the top is the sky
        false);

      // ⚠ AND THEN IT HAS TO COME BACK DOWN.
      //
      //   A ramp that just STOPS at its top is a 30-unit cliff — and
      //   what's at the bottom of that cliff is open deck the ball is
      //   about to cross at 400 units a second. The proof loop found
      //   a 320% grade at (76,224), then a 325% one at (56,252) after
      //   the first fix, and both were the same thing: the last inch
      //   of the Helter-Skelter, with nothing under it.
      //
      //   The RAILS end at the top. The BED must not. A ramp needs a
      //   LANDING — a broad, low mound past the exit that the ball
      //   comes down instead of falls off. Which is what the exit of
      //   a ramp has looked like since 1978.
      //   The landing is a MOUND, and a mound has a grade. Every disc
      //   in it is as tall as the ramp still is at that point, and its
      //   run-out is sized from that height — never steeper than the
      //   ball can roll. A hill, not a shelf with a drop off the side.
      const dx = m1.x - m0.x, dy = m1.y - m0.y;
      const L = Math.hypot(dx, dy) || 1;
      const ux = dx / L, uy = dy / L;
      for (let s = 1; s <= 8; s++) {
        const t = s / 8;
        const h = H0 * (1 - t);                 // ...down to the deck, exactly
        disc(
          m1.x + ux * (EXIT_RUN * t),
          m1.y + uy * (EXIT_RUN * t),
          rIn,
          Math.max(rail, h * 2.4),              // run-out sized from the drop
          h,
          false
        );
      }
      done.add(key);
      continue;
    }
  }

  for (const c of colliders) {
    const key = baseId(c.id);
    const ride = rides.get(key);
    if (!ride || done.has(key)) continue;
    const H0 = RIDE_H[ride.tag] ?? 20;

    // A spiral DESCENDS. Its segments are numbered, so the coil can
    // fall away as it goes — for free, from a name.
    const idx = spiralIndex(c.id);
    const h = (idx != null && ride.maxIdx > 0)
      ? H0 * (1 - 0.6 * (idx / ride.maxIdx))
      : H0;

    if (c.kind === "seg") {
      stamp(c.a.x, c.a.y, c.b.x, c.b.y,
        (c.thickness || 4) / 2 + RIDE_PAD, gradeApron(h), h, h, false);
    } else if (c.kind === "arc") {
      // ⚠ AN ARC IN A RIDE RAISES ITS WHOLE ISLAND, NOT A BAND.
      //   Stamp only a ring under the arc and a loop becomes a raised
      //   donut with a PIT in the middle — which is not a big top,
      //   it's a bundt tin. A loop in a real machine is an elevated
      //   STRUCTURE with a track around it. So: a disc, out to the
      //   outer wall. The ball climbs the mound, rides the ring at
      //   altitude, and gets whipped off the top of it.
      disc(c.c.x, c.c.y, c.r + (c.thickness || 4) / 2 + RIDE_PAD, gradeApron(h), h, false);
    } else if (c.kind === "circle") {
      disc(c.c.x, c.c.y, c.r + RIDE_PAD, gradeApron(h), h, false);
    }
  }

  // ── PASS 3 — dig the wells ─────────────────────────────────
  // Every hole, chute, wormhole, scoop and whirlpool is a circular
  // sensor, and in 3D the deck can simply STOP being flat there.
  // The floor is measured from the ground it's dug into — see the
  // stamp comment. A hole in a hill is a hole in THAT hill.
  for (const c of colliders) {
    if (c.kind !== "circle" || !c.sensor) continue;
    const d = WELL_D[c.tag];
    if (d == null) continue;
    const floor = sampleRaw(c.c.x, c.c.y) + d;
    disc(c.c.x, c.c.y, c.r * 0.85, WELL_APRON, floor, true);
  }

  // ══════════════════════════════════════════════════════════
  //  PASS 4 — THE PLUNGER LANE IS FLAT. NON-NEGOTIABLE.
  // ══════════════════════════════════════════════════════════
  //
  // Spite's Spire ends at x=326. Its landing flares 30 units past
  // that. LANE_X is 370. Do the arithmetic and the ball launches
  // UPHILL, on every ball, forever — a bug that would have shipped
  // and been reported as "the plunger feels weak."
  //
  // The renderer can't import LANE_X (that's a table-layer constant
  // and this file lives above the tables). It doesn't have to: a
  // long vertical wall in the right-hand tenth of a table IS the
  // lane divider. That's not a guess, it's the only thing it can
  // be — and if a table ever has no lane, there's no wall, and this
  // does nothing. Derived, not authored, and it fails safe.
  let laneX = null;
  for (const c of colliders) {
    if (c.kind !== "seg" || c.sensor) continue;
    if (Math.abs(c.b.x - c.a.x) > 3) continue;                 // vertical?
    if (Math.abs(c.b.y - c.a.y) < H * 0.25) continue;          // long?
    if (c.a.x < W * 0.85) continue;                            // right edge?
    laneX = laneX == null ? c.a.x : Math.min(laneX, c.a.x);
  }
  if (laneX != null) {
    const ix0 = Math.floor(laneX / CELL);
    for (let iz = 0; iz < nz; iz++) {
      for (let ix = ix0; ix < nx; ix++) grid[iz * nx + ix] = 0;
    }
  }

  // ── One soft pass to kill the stair-steps ──────────────────
  const out = new Float32Array(grid);
  for (let iz = 1; iz < nz - 1; iz++) {
    for (let ix = 1; ix < nx - 1; ix++) {
      const i = iz * nx + ix;
      out[i] = (
        grid[i] * 4 +
        grid[i - 1] + grid[i + 1] + grid[i - nx] + grid[i + nx]
      ) / 8;
    }
  }

  let maxH = 0;
  for (let i = 0; i < out.length; i++) if (out[i] > maxH) maxH = out[i];

  // Bilinear sample. THE function. Deck, walls, flippers, ball —
  // everything in this file gets its Y from right here.
  const heightAt = (x, y) => {
    const fx = Math.min(nx - 1.001, Math.max(0, x / CELL));
    const fz = Math.min(nz - 1.001, Math.max(0, y / CELL));
    const ix = Math.floor(fx), iz = Math.floor(fz);
    const tx = fx - ix, tz = fz - iz;
    const i = iz * nx + ix;
    const a = out[i], b = out[i + 1], c = out[i + nx], d = out[i + nx + 1];
    return (a * (1 - tx) + b * tx) * (1 - tz) + (c * (1 - tx) + d * tx) * tz;
  };

  return { grid: out, nx, nz, heightAt, maxH };
}

// ══════════════════════════════════════════════════════════════
//  THE DECK TEXTURE — the 2D bake, promoted to a skin
// ══════════════════════════════════════════════════════════════
// Playfield.js's static bake was already beautiful. It doesn't get
// thrown away — it gets WRAPPED AROUND THE TERRAIN. The deck
// gradient, the backbox glow, the inlays, the decor text, and
// every flat sensor decal are painted into one canvas and hung on
// the displaced plane. That's ~60 draw calls saved, and it means
// a table's art still lives in the table's own `decor` array.
function deckTexture(table, colliders, W, H, hostile = false) {
  const S = 2;
  const c = document.createElement("canvas");
  c.width = W * S; c.height = H * S;
  const g = c.getContext("2d");
  g.scale(S, S);

  // ⚠ paint() is the WHOLE deck, re-runnable: the cirque's Blender floor is a picture that arrives a
  //   moment after the table does, so the deck is painted once without it (the code-drawn floor) and
  //   again, with it as the base, the instant it lands. Everything else — lamps, decals, wells, text —
  //   paints on top of whichever base it is, in the same order.
  const paint = (floorImg) => {
  g.setTransform(S, 0, 0, S, 0, 0);
  g.clearRect(0, 0, W, H);

  const grad = g.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, PF.deck1);
  grad.addColorStop(0.55, PF.deck0);
  grad.addColorStop(1, "#02030a");
  g.fillStyle = grad;
  g.fillRect(0, 0, W, H);
  if (floorImg) g.drawImage(floorImg, 0, 0, W, H);   // the Blender flagstones (dressed, dark on purpose)

  // The backbox lamp, spilling down the table.
  const top = g.createRadialGradient(W / 2, -60, 20, W / 2, -60, H * 0.75);
  top.addColorStop(0, "rgba(34,211,238,0.14)");
  top.addColorStop(1, "rgba(34,211,238,0)");
  g.fillStyle = top;
  g.fillRect(0, 0, W, H);

  // Deck grain — seeded, so it is the same grain on every client.
  const R = mulberry32(1337);
  g.globalAlpha = 0.05;
  for (let i = 0; i < 900; i++) {
    const x = R() * W, y = R() * H, s = 1 + R() * 2;
    g.fillStyle = R() > 0.5 ? "#ffffff" : "#000000";
    g.fillRect(x, y, s, s);
  }
  g.globalAlpha = 1;

  // ── THE CIRCUS FLOOR ─────────────────────────────────────────
  // Painted into the same bake as everything else: a sunburst of
  // alternating rays from the heart of the table (the classic
  // big-top ceiling, seen from below), a sawdust centre ring, and
  // a striped valance band around the apron. All low-alpha — the
  // deck is still a playfield first and a poster second.
  if (table.skin === "cirque" && !floorImg) {   // (the Blender floor already has the sunburst, rings and valance)
    const cx = W / 2, cy = H * 0.40;
    g.save();
    for (let i = 0; i < 24; i++) {
      const a0 = (i / 24) * Math.PI * 2;
      const a1 = ((i + 1) / 24) * Math.PI * 2;
      g.fillStyle = i % 2 ? "rgba(217,71,87,0.085)" : "rgba(243,227,195,0.05)";
      g.beginPath();
      g.moveTo(cx, cy);
      g.arc(cx, cy, H, a0, a1);
      g.closePath();
      g.fill();
    }
    // The centre ring — every circus has ONE.
    g.strokeStyle = "rgba(242,193,78,0.30)";
    g.lineWidth = 5;
    g.beginPath(); g.arc(cx, cy, W * 0.30, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = "rgba(217,71,87,0.22)";
    g.lineWidth = 2;
    g.beginPath(); g.arc(cx, cy, W * 0.30 + 7, 0, Math.PI * 2); g.stroke();
    // The valance — a striped band along the apron, above the flippers.
    const bandY = H - 96, bandH = 10, nB = 26;
    for (let i = 0; i < nB; i++) {
      g.fillStyle = i % 2 ? "rgba(217,71,87,0.16)" : "rgba(243,227,195,0.10)";
      g.fillRect((W / nB) * i, bandY, W / nB + 1, bandH);
    }
    g.restore();
  }

  // Inlays (pure art, no collision) — straight from table.decor.
  for (const d of (table.decor || [])) {
    if (d.kind !== "inlay") continue;
    g.fillStyle = PF[d.tint] || PF.deck2;
    g.globalAlpha = 0.55;
    roundRect(g, d.x, d.y, d.w, d.h, 8);
    g.fill();
    g.globalAlpha = 1;
  }

  // Flat sensor decals — rollovers, gates, drains, ride scorers.
  // These are DECALS, not objects: painting them into the deck
  // means zero meshes AND zero z-fighting, because they are the
  // surface rather than a plate hovering 0.1u above it.
  for (const col of colliders) {
    if (!col.sensor) continue;
    if (LAMP_TAGS.has(col.tag) || DEVICE_TAGS.has(col.tag)) continue;
    if (col.tag.startsWith("prize_")) continue;
    if (col.tag === "train") continue;   // W4: a MOVING sensor — a decal baked at its park spot would be a lie
    const base = colorOf(col.tag);
    g.save();
    g.strokeStyle = base;
    g.globalAlpha = 0.30;
    g.lineWidth = 2;
    g.setLineDash([5, 5]);
    g.beginPath();
    if (col.kind === "seg") {
      g.moveTo(col.a.x, col.a.y);
      g.lineTo(col.b.x, col.b.y);
    } else if (col.kind === "circle") {
      g.arc(col.c.x, col.c.y, col.r, 0, Math.PI * 2);
    } else if (col.kind === "arc") {
      g.arc(col.c.x, col.c.y, col.r, col.a0, col.a1);
    }
    g.stroke();
    g.restore();
  }

  // The mouths of the wells. The deck DIPS there in 3D, but a dip
  // lit from above still reads as a bowl — the blackness is what
  // makes it read as an ABSENCE. It is the only thing on the deck
  // darker than the deck, and that is load-bearing.
  for (const col of colliders) {
    if (col.kind !== "circle" || !col.sensor) continue;
    if (WELL_D[col.tag] == null) continue;
    const gr = g.createRadialGradient(col.c.x, col.c.y, 1, col.c.x, col.c.y, col.r * 1.15);
    gr.addColorStop(0, "#000000");
    gr.addColorStop(0.7, "#02030a");
    gr.addColorStop(1, "rgba(2,3,10,0)");
    g.fillStyle = gr;
    g.beginPath();
    g.arc(col.c.x, col.c.y, col.r * 1.15, 0, Math.PI * 2);
    g.fill();
  }

  // Decor text + hints, on top of everything.
  for (const d of (table.decor || [])) {
    if (d.kind === "text") {
      g.save();
      g.fillStyle = PF[d.tint] || PF.goldDim;
      g.font = `900 ${d.size || 18}px ${PF.fontPF}`;
      g.textAlign = "center";
      g.globalAlpha = 0.5;
      if ("letterSpacing" in g) g.letterSpacing = "6px";
      g.fillText(d.text, d.x, d.y);
      g.restore();
    }
    if (d.kind === "hint") {
      g.save();
      g.fillStyle = PF[d.tint] || PF.bone;
      g.font = `700 11px ${PF.fontPF}`;
      g.textAlign = "center";
      g.globalAlpha = 0.65;
      g.fillText(d.text, d.x, d.y);
      g.restore();
    }
  }

  };   // ← end of paint()
  paint(null);

  const t = new THREE.CanvasTexture(c);
  // ⚠ WITHOUT THIS EVERY COLOUR COMES OUT PALE AND YOU WILL SPEND AN
  //   HOUR BLAMING THE PALETTE. Canvas textures default to linear.
  if (THREE.SRGBColorSpace) t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  if (table.skin === "cirque" && !hostile) {   // the little opponent table never pays for the floor picture
    const im = new Image();
    im.onload = () => { if (t.userData.dead) return; paint(im); t.needsUpdate = true; };
    im.onerror = () => console.warn("[deck] the Blender floor did not load — the code-drawn floor stands");
    im.src = `${process.env.PUBLIC_URL || ""}/models/tilt_deck_floor.jpg`;
  }
  return t;
}

// The marquee art — the table's name, lit. It used to hang in a
// backbox behind the table; it now hangs on the two floating side
// banners. Same sign, better real estate.
function backboxTexture(table) {
  const c = document.createElement("canvas");
  c.width = 800; c.height = 400;
  const g = c.getContext("2d");

  // ── THE CIRQUE MARQUEE ───────────────────────────────────────
  // Crimson curtain, cream sunburst, a border of warm bulbs, and
  // the show's name in circus lettering. This is the sign the
  // player reads from across the arcade.
  if (table.skin === "cirque") {
    const bg = g.createLinearGradient(0, 0, 0, 400);
    bg.addColorStop(0, "#3a1220");
    bg.addColorStop(1, "#160710");
    g.fillStyle = bg;
    g.fillRect(0, 0, 800, 400);
    // Sunburst rays behind the lettering.
    g.save();
    g.translate(400, 210);
    for (let i = 0; i < 20; i++) {
      const a0 = (i / 20) * Math.PI * 2, a1 = ((i + 1) / 20) * Math.PI * 2;
      g.fillStyle = i % 2 ? "rgba(217,71,87,0.30)" : "rgba(243,227,195,0.12)";
      g.beginPath(); g.moveTo(0, 0); g.arc(0, 0, 520, a0, a1); g.closePath(); g.fill();
    }
    g.restore();
    // Warm centre glow.
    const gl = g.createRadialGradient(400, 200, 20, 400, 200, 380);
    gl.addColorStop(0, "rgba(255,217,160,0.30)");
    gl.addColorStop(1, "rgba(255,217,160,0)");
    g.fillStyle = gl;
    g.fillRect(0, 0, 800, 400);
    // The name — cream with a crimson understroke, arched feel via
    // a heavy outline. (Canvas can't bend text cheaply; the outline
    // and the rays do the carnival work.)
    g.textAlign = "center";
    g.lineJoin = "round";
    g.font = `900 84px ${PF.fontPF}`;
    if ("letterSpacing" in g) g.letterSpacing = "6px";
    g.strokeStyle = "#7c1f2e";
    g.lineWidth = 14;
    g.strokeText(String(table.name || "TILT").toUpperCase(), 400, 205);
    g.fillStyle = "#f3e3c3";
    g.fillText(String(table.name || "TILT").toUpperCase(), 400, 205);
    g.fillStyle = "#f2c14e";
    g.font = `700 26px ${PF.fontPF}`;
    if ("letterSpacing" in g) g.letterSpacing = "4px";
    g.globalAlpha = 0.85;
    g.fillText(String(table.blurb || "").toUpperCase().slice(0, 46), 400, 262);
    g.globalAlpha = 1;
    // The bulb border — the marquee's whole personality.
    const bulb = (x, y, on) => {
      g.fillStyle = on ? "#ffd9a0" : "#6b4a2e";
      g.beginPath(); g.arc(x, y, 7, 0, Math.PI * 2); g.fill();
      if (on) {
        const bb = g.createRadialGradient(x, y, 2, x, y, 18);
        bb.addColorStop(0, "rgba(255,217,160,0.5)");
        bb.addColorStop(1, "rgba(255,217,160,0)");
        g.fillStyle = bb;
        g.beginPath(); g.arc(x, y, 18, 0, Math.PI * 2); g.fill();
      }
    };
    let k = 0;
    for (let x = 24; x <= 776; x += 47) { bulb(x, 22, k % 2 === 0); k++; }
    for (let x = 24; x <= 776; x += 47) { bulb(x, 378, k % 2 === 0); k++; }
    for (let y = 69; y <= 331; y += 52.4) { bulb(24, y, k % 2 === 0); k++; }
    for (let y = 69; y <= 331; y += 52.4) { bulb(776, y, k % 2 === 0); k++; }
    const t = new THREE.CanvasTexture(c);
    if (THREE.SRGBColorSpace) t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  const grad = g.createLinearGradient(0, 0, 0, 400);
  grad.addColorStop(0, "#0a0d1c");
  grad.addColorStop(1, "#02030a");
  g.fillStyle = grad;
  g.fillRect(0, 0, 800, 400);

  const glow = g.createRadialGradient(400, 200, 20, 400, 200, 380);
  glow.addColorStop(0, "rgba(34,211,238,0.22)");
  glow.addColorStop(1, "rgba(34,211,238,0)");
  g.fillStyle = glow;
  g.fillRect(0, 0, 800, 400);

  g.textAlign = "center";
  g.fillStyle = PF.gold;
  g.font = `900 92px ${PF.fontPF}`;
  if ("letterSpacing" in g) g.letterSpacing = "10px";
  g.fillText(String(table.name || "TILT").toUpperCase(), 400, 210);

  g.fillStyle = PF.neon;
  g.font = `700 26px ${PF.fontPF}`;
  if ("letterSpacing" in g) g.letterSpacing = "4px";
  g.globalAlpha = 0.75;
  g.fillText(String(table.blurb || "").toUpperCase().slice(0, 46), 400, 268);

  const t = new THREE.CanvasTexture(c);
  if (THREE.SRGBColorSpace) t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function roundRect(g, x, y, w, h, r) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

// ══════════════════════════════════════════════════════════════
//  LAYER 4 — OBJECTS. One 3D thing per collider.
// ══════════════════════════════════════════════════════════════
// A wall is cut into CHUNK-long boxes so that it can CLIMB. A
// single box can't bend, so a rail on a ramp would float at one
// end and bury itself at the other. Chunked, it walks the terrain.
//
// And an arc is cut the same way — which is why the loop's walls
// BANK with the bed they're standing on, instead of hovering over
// it in a perfect flat circle.

function chunkSeg(ax, ay, bx, by, thick, tag, id, heightAt, lowDetail) {
  const out = [];
  const L = Math.hypot(bx - ax, by - ay) || 1;
  const n = lowDetail ? 1 : Math.max(1, Math.round(L / CHUNK));
  const h = wallH(tag);
  for (let i = 0; i < n; i++) {
    const t0 = i / n, t1 = (i + 1) / n;
    const x0 = ax + (bx - ax) * t0, y0 = ay + (by - ay) * t0;
    const x1 = ax + (bx - ax) * t1, y1 = ay + (by - ay) * t1;
    const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
    const base = heightAt(mx, my);
    const len = Math.hypot(x1 - x0, y1 - y0) + (n > 1 ? 2.5 : 0);
    out.push({
      kind: "box", tag, id: `${id}#${i}`,
      x: mx, y: my,
      rotY: Math.atan2(-(y1 - y0), x1 - x0),
      len, thick: thick || 4,
      bottom: base - SKIRT, top: base + h,
      h,                                   // the VISIBLE height (the Blender wall scales by it)
    });
  }
  return out;
}

function chunkArc(cx, cy, r, a0, a1, thick, tag, id, heightAt, lowDetail) {
  const out = [];
  const span = a1 - a0;
  const arcLen = Math.abs(span) * r;
  const n = lowDetail
    ? Math.max(3, Math.round(arcLen / (CHUNK * 3)))
    : Math.max(4, Math.round(arcLen / CHUNK));
  for (let i = 0; i < n; i++) {
    const b0 = a0 + (span * i) / n;
    const b1 = a0 + (span * (i + 1)) / n;
    const x0 = cx + Math.cos(b0) * r, y0 = cy + Math.sin(b0) * r;
    const x1 = cx + Math.cos(b1) * r, y1 = cy + Math.sin(b1) * r;
    out.push(...chunkSeg(x0, y0, x1, y1, thick, tag, `${id}~${i}`, heightAt, true));
  }
  return out;
}

// ── Build every static part, once, per table ──────────────────
function buildParts(world, heightAt, lowDetail) {
  const parts = [];       // dumb boxes + cylinders — the bulk
  const lamps = [];       // the things that light up
  const devices = [];     // holes, scoops, pools, chutes
  const specials = [];    // bumpers, captives — bespoke bodies

  for (const c of world.colliders) {
    if (c.tag === "flipper" || c.tag === "flipper_post") continue;
    if (c.tag === "anchor" || c.tag === "anchor_hostile") continue;
    if (MOVING_TAGS.has(c.tag)) continue;    // drawn LIVE. See <Movers>.

    // ── LAMPS — one mesh each, emissive driven by table.lamps() ──
    //
    // ⚠ A TARGET IS NOT A SENSOR. `targetBank()` builds SOLID segs
    //   (restitution 0.6 — the ball BOUNCES off a drop target), and
    //   so does a grudge. Only nodes, prize slots, the crucible and
    //   the spinner are sensors. Gate this branch on `c.sensor` and
    //   every target on every table silently falls through to the
    //   plain-wall path and NEVER LIGHTS UP — which is the one thing
    //   a target exists to do.
    //
    //   So the branch is on the TAG, and the collider keeps whatever
    //   body its kind demands. A solid target is a lit WALL. A node
    //   is a lit PUCK. The lamp is the lighting, not the geometry.
    if (LAMP_TAGS.has(c.tag) || c.tag.startsWith("prize_")) {
      const seg = c.kind === "seg";
      const x = seg ? (c.a.x + c.b.x) / 2 : (c.c?.x ?? 0);
      const y = seg ? (c.a.y + c.b.y) / 2 : (c.c?.y ?? 0);
      const base = heightAt(x, y);
      lamps.push({
        id: c.id, tag: c.tag, x, y, base,
        solid: !c.sensor,                       // may it move? NO.
        cyl: !seg,
        len: seg ? Math.hypot(c.b.x - c.a.x, c.b.y - c.a.y) : 0,
        thick: c.thickness || 8,
        r: seg ? 0 : (c.r ?? 9),
        rotY: seg ? Math.atan2(-(c.b.y - c.a.y), c.b.x - c.a.x) : 0,
        // A solid lamp stands like the wall it is. A sensor lamp is a
        // massless decal and may bob and pulse — it can't lie about
        // physics it doesn't have.
        h: c.sensor ? 3.6 : wallH(c.tag),
        buried: !c.sensor,
      });
      continue;
    }

    // Devices — a hole must look like nothing at all.
    if (c.sensor && DEVICE_TAGS.has(c.tag)) {
      devices.push({
        id: c.id, tag: c.tag,
        x: c.c?.x ?? 0, y: c.c?.y ?? 0, r: c.r ?? 10,
        base: heightAt(c.c?.x ?? 0, c.c?.y ?? 0),
      });
      continue;
    }

    // Every other sensor is a flat decal, already painted into the
    // deck. No mesh. No z-fight. No cost.
    if (c.sensor) continue;

    if (c.kind === "seg") {
      // A ghostable wall reads SEMI-TRANSPARENT — the visual promise
      // that it isn't quite real. The player must be able to SEE the
      // lock in order to go looking for the key.
      const chunks = chunkSeg(
        c.a.x, c.a.y, c.b.x, c.b.y, c.thickness, c.tag, c.id, heightAt, lowDetail
      );
      if (c.ghostable) for (const k of chunks) k.ghost = true;
      parts.push(...chunks);
      continue;
    }

    if (c.kind === "arc") {
      parts.push(...chunkArc(
        c.c.x, c.c.y, c.r, c.a0, c.a1, c.thickness, c.tag, c.id, heightAt, lowDetail
      ));
      continue;
    }

    if (c.kind === "circle") {
      if (c.tag === "bumper" || c.tag === "captive") {
        specials.push({
          id: c.id, tag: c.tag, x: c.c.x, y: c.c.y, r: c.r,
          base: heightAt(c.c.x, c.c.y),
        });
      } else {
        parts.push({
          kind: "cyl", tag: c.tag, id: c.id,
          x: c.c.x, y: c.c.y, r: c.r,
          bottom: heightAt(c.c.x, c.c.y) - SKIRT,
          top: heightAt(c.c.x, c.c.y) + wallH(c.tag),
        });
      }
      continue;
    }
  }

  return { parts, lamps, devices, specials };
}

// ══════════════════════════════════════════════════════════════
//  THE TABLE GEOMETRY — built once per engine, memoised
// ══════════════════════════════════════════════════════════════
function buildTableGeo(eng, lowDetail) {
  const table = eng.table;
  const W = table.W ?? 400;
  const H = table.H ?? 700;
  const hf = buildHeightfield(eng.world.colliders, W, H);
  const { heightAt } = hf;

  // The deck: a plane, displaced by the height field. This single
  // mesh IS the third dimension — everything else just rides it.
  const deckGeo = new THREE.PlaneGeometry(W, H, hf.nx - 1, hf.nz - 1);
  deckGeo.rotateX(-Math.PI / 2);
  const pos = deckGeo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), z = pos.getZ(i);
    pos.setY(i, heightAt(x + W / 2, z + H / 2));
  }
  pos.needsUpdate = true;
  deckGeo.computeVertexNormals();

  const deckTex = deckTexture(table, eng.world.colliders, W, H, lowDetail);
  const backTex = backboxTexture(table);
  const built = buildParts(eng.world, heightAt, lowDetail);
  const skin = table.skin || null;

  // ── LANDMARKS — where the dressing layer hangs its decoration ─
  // Derived, never authored, same law as the height field: a loop
  // is a big top, so a loop's centre gets a canopy; a prize wheel
  // is a carousel, so the wheel's hub gets one too. Add a loop to
  // any cirque-skinned table tomorrow and the tent shows up free.
  const landmarks = { tents: [] };
  if (skin === "cirque") {
    const seen = new Set();
    for (const c of eng.world.colliders) {
      if (c.tag !== "loop" || c.kind !== "arc") continue;
      const key = `${Math.round(c.c.x)},${Math.round(c.c.y)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      landmarks.tents.push({
        x: c.c.x, y: c.c.y, r: c.r,
        base: heightAt(c.c.x, c.c.y) + wallH("loop"),
      });
    }
    // The prize wheel's hub — centroid of its prize_* lamps.
    const pz = built.lamps.filter((l) => l.tag.startsWith("prize_"));
    if (pz.length >= 3) {
      const cx = pz.reduce((a, l) => a + l.x, 0) / pz.length;
      const cy = pz.reduce((a, l) => a + l.y, 0) / pz.length;
      const rr = Math.max(...pz.map((l) => Math.hypot(l.x - cx, l.y - cy)));
      landmarks.tents.push({
        x: cx, y: cy, r: Math.max(16, rr * 0.6),
        base: heightAt(cx, cy) + 12,
      });
    }
  }

  // Shared materials, one per tag. Sharing them is what keeps a
  // 400-mesh table from thrashing the GPU's state machine.
  const mats = new Map();
  const matFor = (tag, ghost) => {
    const key = `${tag}${ghost ? "|g" : ""}`;
    if (mats.has(key)) return mats.get(key);
    const col = new THREE.Color(skinColorOf(skin, tag));
    const m = new THREE.MeshStandardMaterial({
      color: col,
      emissive: col,
      emissiveIntensity: tag === "wall" ? 0.06 : 0.22,
      metalness: 0.45,
      roughness: 0.42,
      transparent: !!ghost,
      opacity: ghost ? 0.42 : 1,
    });
    mats.set(key, m);
    return m;
  };

  return {
    W, H, table, hf, heightAt, deckGeo, deckTex, backTex, matFor, mats,
    skin, landmarks,
    ...built,
    maxH: hf.maxH,
    tx: (x) => x - W / 2,       // table → world
    tz: (y) => y - H / 2,
    dispose() {
      deckGeo.dispose();
      deckTex.userData.dead = true;     // a floor image landing after disposal must not re-upload it
      deckTex.dispose();
      backTex.dispose();
      for (const m of mats.values()) m.dispose();
    },
  };
}

// Unit primitives — ONE geometry, scaled a hundred ways. This is
// why a table with 400 walls costs 3 geometries.
const GEO = {
  box: new THREE.BoxGeometry(1, 1, 1),
  cyl: new THREE.CylinderGeometry(1, 1, 1, 14),
  cone: new THREE.ConeGeometry(1, 1, 18),
  sphere: new THREE.SphereGeometry(1, 18, 14),
  ring: new THREE.RingGeometry(0.72, 1, 28),
  disc: new THREE.CircleGeometry(1, 28),
};

// ══════════════════════════════════════════════════════════════
//  LAYER 2 — ENVIRONMENT & THE CABINET
// ══════════════════════════════════════════════════════════════
// ⚠ THE DIORAMA BUG. Without this the table is a slab floating in
//   a void and the whole thing reads as a model on a shelf. A
//   cabinet costs six boxes and buys the entire illusion that the
//   ball is somewhere REAL.
function Cabinet({ geo, hostile }) {
  const { W, H, maxH, backTex, skin } = geo;
  const haveAll = useHave();       // the code-drawn rails step aside only once the Blender wagon has landed
  const have = hostile ? {} : haveAll;   // ...but only on the MAIN table: the duel's opponent table never loads the glbs
  const B = bannerLayout({ W, H, maxH, skin });
  const railH = 20, railT = 14, bodyD = 74;
  // The cirque cabinet is a painted wagon — deep crimson lacquer
  // instead of arcade steel. Same six boxes; only the paint moved.
  const cirque = skin === "cirque";
  const dim = useMemo(() => new THREE.MeshStandardMaterial({
    color: cirque ? "#180a14" : "#0d1120", metalness: 0.7, roughness: 0.5,
  }), [cirque]);
  const brass = useMemo(() => new THREE.MeshStandardMaterial({
    color: cirque ? "#63202f" : "#3a3f5c",
    metalness: cirque ? 0.55 : 0.9,
    roughness: cirque ? 0.45 : 0.28,
  }), [cirque]);
  useEffect(() => () => { dim.dispose(); brass.dispose(); }, [dim, brass]);

  const rails = [
    { x: -W / 2 - railT / 2, z: 0, w: railT, d: H + railT * 2 },
    { x:  W / 2 + railT / 2, z: 0, w: railT, d: H + railT * 2 },
    { x: 0, z: -H / 2 - railT / 2, w: W + railT * 2, d: railT },
    { x: 0, z:  H / 2 + railT / 2, w: W + railT * 2, d: railT },
  ];

  return (
    <group>
      {rails.map((r, i) => (
        <mesh key={i} geometry={GEO.box} material={brass} visible={!have.cabinet}
          position={[r.x, (railH - bodyD) / 2, r.z]}
          scale={[r.w, railH + bodyD, r.d]} castShadow receiveShadow />
      ))}
      {/* The body. No top face — so a hole looks into the dark. */}
      <mesh geometry={GEO.box} material={dim}
        position={[0, -bodyD - 4, 0]} scale={[W + railT * 2, 8, H + railT * 2]}
        receiveShadow />
      {/* ── THE FLOATING BANNERS ─────────────────────────────
          The marquee used to be a BACKBOX — a second storey stacked
          on top of the machine. It cost the shot a third of its
          height: the camera had to fit the sign, so the TABLE
          shrank. The sign now hangs in the air BESIDE the machine,
          once per flank, filling exactly the space the widescreen
          shot was wasting — and the table gets its whole frame back,
          top to bottom.

          Its numbers live in bannerLayout() (beside the lenses)
          because the CAMERA has to know about these things too — a
          sign the shot doesn't know about is a sign it will
          guillotine. One function, two consumers: this mesh and
          cornersOf(). Same law the backbox lived by. */}
      {[-1, 1].map((s) => (
        <group key={s}
          position={[s * B.x, B.y, B.z]}
          rotation={[B.tilt, -s * B.yaw, 0]}>
          <mesh geometry={GEO.box} material={brass}
            position={[0, 0, -B.d / 2 - 1]}
            scale={[B.w + 10, B.h + 10, B.d]} />
          <mesh position={[0, 0, 1]}>
            <planeGeometry args={[B.w, B.h]} />
            <meshBasicMaterial map={backTex} toneMapped={false} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function Lights({ geo, hostile }) {
  const { W, H, skin } = geo;
  // A circus is lit WARM. The neon-cathedral rig stays for every
  // other table; the cirque swaps its sky and its rim for tungsten
  // and rose, and hangs one extra lamp over the centre ring.
  const cirque = skin === "cirque";
  return (
    <>
      {/* Deliberately generous ambient. Realism wants dark corners.
          A PLAYFIELD wants every shot legible from any angle. */}
      <ambientLight intensity={0.42} />
      <hemisphereLight args={cirque
        ? ["#ffd9a0", "#170a12", 0.40]
        : ["#22d3ee", "#0a0d1c", 0.35]} />
      <directionalLight
        position={[-W * 0.5, 520, -H * 0.55]}
        intensity={1.15}
        castShadow={!hostile}
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-left={-W}
        shadow-camera-right={W}
        shadow-camera-top={H * 0.8}
        shadow-camera-bottom={-H * 0.8}
        shadow-camera-far={1600}
        shadow-bias={-0.0006}
        shadow-normalBias={0.6}
      />
      {/* THE RIM LIGHT. This is the cheat code. One cool light from
          the opposite side puts an edge on every silhouette and is
          single-handedly why the table reads as LIT and not shaded. */}
      <directionalLight position={[W * 0.7, 180, H * 0.8]} intensity={0.5}
        color={cirque ? "#ff9d6a" : PF.violet} />
      {/* The marquee-end lamp — warms the top arch. (It survived
          the backbox; the arch still deserves its glow.) */}
      <pointLight position={[0, 150, -H * 0.42]} intensity={hostile ? 0.5 : 1.1}
        distance={900} decay={1.4} color={cirque ? CIRQUE.bulb : PF.neon} />
      <pointLight position={[0, 90, H * 0.36]} intensity={0.55}
        distance={520} decay={1.6} color={PF.gold} />
      {/* The ring lamp — one warm bulb over the centre of the show. */}
      {cirque && !hostile && (
        <pointLight position={[0, 130, -H * 0.10]} intensity={0.6}
          distance={620} decay={1.7} color={CIRQUE.bulb} />
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════
//  LAYER 6 — THE CAMERA. The biggest decision in the file.
// ══════════════════════════════════════════════════════════════
//
//  THE STAGE, with an escape hatch.
//
//  A pinball table is the one game object in the world that has
//  ALREADY had its camera solved — by seventy years of cabinet
//  builders, and by the fact that a machine you can orbit is a
//  machine you fall off. During play the lens is NAILED DOWN. You
//  drive the ball. You do not drive the camera.
//
//  DELETE THE DEGREE OF FREEDOM, NOT THE BUG: there is no orbit
//  during play, so there is no "camera swung under the deck", no
//  "lost the ball behind the big top", no clamp, no sweep, no
//  guardrail. Those bugs are not fixed. They are unrepresentable.
//
//  FREE mode exists because caP'n will want to LOOK at the thing
//  he built, and that is a different activity from playing it.
//  Four buttons = four LENSES, not four orbits.
//
//  ── AND THE SHOT IS SOLVED, NOT TUNED ────────────────────────
//  Every hand-picked camera number is a bug waiting for a screen
//  you don't own. Here the framing is BINARY-SEARCHED against the
//  live aspect ratio until all eight corners of the table's
//  bounding box are inside the frustum. A phone, an ultrawide, and
//  a duel's half-width panel all get the same composition, and
//  none of them get it because someone eyeballed a number.
const FOV = 40;   // the lens. ONE number — the solver below and the
                  // real camera at the bottom of the file both read it.

// The lens is a DIRECTION and an AIM HINT. It is not a position and
// it is not a distance — those are solved. `look` is only the seed
// the solver starts panning from; where it ends up is derived.
const LENSES = {
  // PLAY was re-aimed 2026-07-14 to match a shot caP'n composed in
  // FREE mode: much steeper (≈49° vs the old 32°), reading the whole
  // deck like a map while keeping enough rake that ramps and the big
  // top still have height. The banners are deliberately NOT in this
  // shot's hull anymore (see cornersOf) — they crop at the frame's
  // flanks, exactly as they did in the reference screenshot, and the
  // table gets the full frame.
  play: { dir: [0, 1.15, 1.00], look: [0, 6, 12], margin: 1.00 },
  top:  { dir: [0, 1.00, 0.10], look: [0, 0, 0],   margin: 0.98 },
};

// ── THE BANNERS' LAYOUT — one function, two consumers ─────────
// The mesh (Cabinet) and the camera hull (cornersOf) both read
// THIS, so they cannot disagree — the same one-constant-two-
// consumers law the old BACKBOX lived by, now a function because
// the banners size themselves to the machine they hang beside.
//
// The ceiling here is cornersOf()'s ceiling, on purpose: a banner
// is only allowed to be as tall as the hull ALREADY is. If it ever
// poked above the table's own ceiling it would become the binding
// vertical constraint, the solver would pull back to fit it, and
// the table would shrink — which is the exact backbox disease this
// redesign exists to cure. The banners may only ever spend the
// frame's WIDTH. The height belongs to the table.
// ⚠ THE MARQUEE LAW: dressing is part of the machine. The cirque's boss
//   (SetPieces3D) stands BOSS_H tall on deck the ball can reach, so the
//   ceiling both the rig and the frame read must clear him. Same
//   ceiling, one function, both consumers — never raise only the mesh.
const ceilOf = (maxH, skin) => (skin === "cirque" ? Math.max(maxH + 26, BOSS_TOP + 8) : maxH + 26);

function bannerLayout({ W, H, maxH, skin }) {
  const ceil = ceilOf(maxH, skin);
  const usable = (ceil - 4) + 20;             // floor −20 … ceiling −4
  const h = Math.min(90, usable);
  const w = h * 2;                            // the art is 800×400 — 2:1
  const y = Math.min(45, (ceil - 4) - h / 2); // as high as fits, capped
  return {
    h, w, y,
    x: W / 2 + 14 + 26 + w / 2,   // rail (14) + air (26) + half the sign
    z: -H * 0.10,                 // upper-middle of the table's length
    yaw: 0.30,                    // angled inward, facing the lens
    tilt: -0.08,                  // leaned back a touch — a standing sign
    d: 8,                         // the frame's depth
  };
}

// ── THE HULL. ONE definition, every consumer. ─────────────────
// The rig solves DISTANCE and AIM against these corners; the frame
// solves its own SHAPE against the same ones. If they ever
// disagreed the table would be framed for a machine it isn't
// standing in — so they don't get to disagree.
//
// ⚠ THE MARQUEE IS PART OF THE MACHINE. It taught us this twice:
//   the old hull stopped at maxH + 40 while the backbox reached
//   ~135, and the sign only stayed on screen by accident. The sign
//   is banners on the FLANKS now, and the lesson holds unchanged —
//   they are in the hull, from the SAME numbers the meshes are
//   built from (bannerLayout), or the frame is solved for a
//   machine the sign isn't hanging beside.
function cornersOf({ W, H, maxH, skin }, mode = "play") {
  // ⚠ THE DRESSING IS PART OF THE MACHINE — same law as the
  //   marquee. The cirque hangs bunting and tent finials well above
  //   the tallest wall; a hull that doesn't know raises the same
  //   guillotine bug the backbox already taught us about. One
  //   ceiling, skin-aware, both consumers (rig + frame) inherit it.
  const ceil = ceilOf(maxH, skin);
  const out = [];
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      for (const sy of [0, 1]) {
        out.push(new THREE.Vector3(
          sx * (W / 2 + 16),
          sy ? ceil : -28,
          sz * (H / 2 + 18)
        ));
      }
    }
  }
  // The banners are NOT in the hull — for ANY lens. This is the
  // 2026-07-14 re-composition: caP'n framed the shot he wanted in
  // FREE mode, and in it the banners crop at the frame's flanks
  // while the TABLE spends the whole frame. That is the composition.
  // The old play-only banner AABB block that used to live here made
  // the solver pull back to fit the signs; deleting it is what buys
  // the edge-to-edge table. The banners still render — they are
  // decoration, and decoration is allowed to crop.
  return out;
}

// ══ THE SHOT SOLVER ═══════════════════════════════════════════
//
//  THE PICTURE DOES NOT SIT WHERE THE CAMERA POINTS. Aim a lens at
//  the middle of a table from above and behind, and the machine
//  does NOT land in the middle of the frame — the far end is far
//  and small, the apron is near and huge, and the whole silhouette
//  slides off-centre. Fit that by asking "is every corner inside
//  ±margin", which is what a naive rig does, and you are fitting
//  the picture into a box CENTRED ON THE WRONG PLACE: it shoves the
//  camera back until the worst-offending corner squeezes in, and
//  pays for it with a dead band on the opposite side. That dead
//  band was the black you were looking at.
//
//  So: PAN, don't retreat. Project the hull, find where its centre
//  actually landed, and slide the aim point sideways/up by exactly
//  that much — in the CAMERA's own basis, so the angle of the shot
//  is untouched. Now re-solve the distance. Two or three passes and
//  the hull is dead-centre and touching all four edges.
const _t = () => Math.tan((FOV * Math.PI / 180) / 2);

const _probe = new THREE.PerspectiveCamera(FOV, 1, 5, 8000);   // reused; the solve is synchronous
const _m = new THREE.Matrix4();
const _v = new THREE.Vector3();

function project(corners, eye, tgt, aspect) {
  const cam = _probe;
  cam.aspect = aspect;
  cam.position.copy(eye);
  cam.lookAt(tgt);
  cam.updateMatrixWorld(true);
  cam.updateProjectionMatrix();
  const m = _m.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
  for (const c of corners) {
    const p = _v.copy(c).applyMatrix4(m);     // ← does the perspective divide
    x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x);
    y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y);
  }
  return { x0, x1, y0, y1, w: x1 - x0, h: y1 - y0, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
}

// How far back must the lens stand for the hull to clear the edges?
// Binary search. 24 iterations. Proven, not eyeballed.
function solveDist(corners, dir, tgt, aspect, margin) {
  const eye = new THREE.Vector3();
  const fits = (D) => {
    eye.copy(tgt).addScaledVector(dir, D);
    const p = project(corners, eye, tgt, aspect);
    return Math.max(Math.abs(p.x0), Math.abs(p.x1)) <= margin
        && Math.max(Math.abs(p.y0), Math.abs(p.y1)) <= margin;
  };
  // The floor of the search is the hull's own radius: past it, every
  // corner is provably IN FRONT of the lens. Inside it, corners fall
  // behind the camera, the perspective divide flips their sign, and
  // a point that is behind you can look exactly like a point that
  // fits. Never let the search go there.
  let lo = 40;
  for (const c of corners) lo = Math.max(lo, c.distanceTo(tgt) + 20);
  let hi = 8000;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) hi = mid; else lo = mid;
  }
  return hi;
}

// Slide the aim point until the hull's centre IS the frame's centre.
function recentre(corners, dir, tgt, D, aspect) {
  const eye = tgt.clone().addScaledVector(dir, D);
  const p = project(corners, eye, tgt, aspect);
  const fwd = tgt.clone().sub(eye).normalize();
  const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 1, 0)).normalize();
  const up = new THREE.Vector3().crossVectors(right, fwd);
  const t = _t();
  return tgt.clone()
    .addScaledVector(right, p.cx * D * t * aspect)   // NDC → world, at the aim plane
    .addScaledVector(up,    p.cy * D * t);
}

// AIM + DISTANCE, at a KNOWN frame shape. This is what the rig runs.
function fitCamera(corners, L, aspect) {
  const dir = new THREE.Vector3(...L.dir).normalize();
  let tgt = new THREE.Vector3(...L.look);
  let D = solveDist(corners, dir, tgt, aspect, L.margin);
  for (let i = 0; i < 3; i++) {
    tgt = recentre(corners, dir, tgt, D, aspect);
    D = solveDist(corners, dir, tgt, aspect, L.margin);
  }
  return { dir, tgt, dist: D };
}

// ── AND THE FRAME IS SHAPED LIKE THE SHOT ────────────────────
//
//  THE OTHER HALF OF THE BUG. The 2D renderer drew a 400×700 table
//  into a 400×700 box — the box WAS the table, nothing to waste. A
//  camera doesn't work like that: foreshortening eats the depth, so
//  the PICTURE of a tall table shot from behind is nearly SQUARE.
//  Pour it into a portrait window and it fits by width and
//  letterboxes by height. Half the frame goes black.
//
//  So the window stops being the shape of the TABLE and becomes the
//  shape of the SHOT — and that shape is not something you can
//  measure from a safe distance, because the aspect of the picture
//  DEPENDS on how close the camera is, and how close the camera is
//  DEPENDS on the aspect of the frame. It's a fixpoint. So solve it
//  like one: guess square, fit the camera, measure what the picture
//  actually came out as, feed that back as the new frame, repeat.
//  Five passes and it stops moving.
//
//  Derived, per table, per lens: PLAY lands near 1.10 on a 400×700,
//  TOP wants a portrait, CAB wants a hard letterbox. Change a
//  table's dimensions tomorrow, or the size of the banners, and
//  every number in here re-derives itself. None of them can rot.
function shotAspect(geo, mode) {
  const L = LENSES[mode] || LENSES.play;
  const corners = cornersOf(geo, mode);
  let aspect = 1;
  for (let i = 0; i < 5; i++) {
    const { dir, tgt, dist } = fitCamera(corners, L, aspect);
    const p = project(corners, tgt.clone().addScaledVector(dir, dist), tgt, aspect);
    aspect *= p.w / Math.max(1e-6, p.h);       // NDC x is already aspect-divided
  }
  return Math.min(2.70, Math.max(0.50, aspect));   // sanity rails, never reached in practice
}

function CameraRig({ geo, mode, engineRef, hostile }) {
  const { camera, size } = useThree();
  const goal = useRef({
    pos: new THREE.Vector3(0, 600, 700),
    tgt: new THREE.Vector3(0, 0, 0),
    active: true,
    snap: true,
  });
  const shake = useRef(0);
  const lastTilt = useRef(0);

  // Solve the shot against the LIVE canvas — not the ideal frame.
  // When the frame honours the aspect shotAspect() asked for, this
  // lands exactly on the ideal composition. When it CAN'T — a duel's
  // half-width panel, a phone, a window mid-drag — it still centres
  // the machine and still touches the binding edge. It degrades; it
  // never breaks.
  useEffect(() => {
    if (mode === "free") return;
    const L = LENSES[mode] || LENSES.play;
    const aspect = size.width / Math.max(1, size.height);
    const { dir, tgt, dist } = fitCamera(cornersOf(geo, mode), L, aspect);

    const g = goal.current;
    g.pos.copy(tgt).addScaledVector(dir, dist);
    g.tgt.copy(tgt);
    g.active = true;
  }, [mode, geo, size.width, size.height]);

  useFrame((_, dt) => {
    const e = engineRef.current;
    const g = goal.current;

    // The table is being ABUSED. Show it.
    if (e) {
      if (e.tilt > lastTilt.current) shake.current = 1;
      lastTilt.current = e.tilt;
      if (e.phase === "tilted") shake.current = Math.max(shake.current, 0.55);
    }
    shake.current = Math.max(0, shake.current - dt * 1.8);

    if (mode !== "free" && g.active) {
      const k = g.snap ? 1 : 0.075;
      camera.position.lerp(g.pos, k);
      const t = new THREE.Vector3().copy(g.tgt);
      if (shake.current > 0.001 && !hostile) {
        const s = shake.current * 9;
        t.x += (Math.random() - 0.5) * s;
        t.y += (Math.random() - 0.5) * s;
      }
      camera.lookAt(t);
      g.snap = false;
      if (camera.position.distanceTo(g.pos) < 0.4 && shake.current <= 0.001) g.active = false;
    }
  });

  return null;
}

// ══════════════════════════════════════════════════════════════
//  THE DECK — and the aim raycast
// ══════════════════════════════════════════════════════════════
// A power is aimed by CLICKING THE TABLE, and that still has to
// work with the lens wherever it is. So we don't invert a
// projection — we let three.js raycast the actual deck mesh and
// hand us the actual point. Camera-agnostic by construction.
function Deck({ geo, aiming, onAim, engineRef }) {
  const { deckGeo, deckTex, W, H } = geo;
  const mat = useMemo(() => new THREE.MeshStandardMaterial({
    map: deckTex,
    emissiveMap: deckTex,        // ← the whole table glows where it's painted
    emissive: new THREE.Color("#5b7dd8"),
    emissiveIntensity: 0.30,
    metalness: 0.28,
    roughness: 0.72,
  }), [deckTex]);
  useEffect(() => () => mat.dispose(), [mat]);

  const toTable = (p) => ({ x: p.x + W / 2, y: p.z + H / 2 });

  return (
    <mesh
      geometry={deckGeo}
      material={mat}
      receiveShadow
      onPointerMove={aiming ? (ev) => {
        ev.stopPropagation();
        const e = engineRef.current;
        if (e) e._aimHover = toTable(ev.point);
      } : undefined}
      onClick={aiming ? (ev) => {
        ev.stopPropagation();
        onAim?.(toTable(ev.point));
      } : undefined}
    />
  );
}

// ══════════════════════════════════════════════════════════════
//  STATIC GEOMETRY — walls, rails, posts
// ══════════════════════════════════════════════════════════════
function Statics({ geo, hostile }) {
  const { parts, matFor, tx, tz } = geo;
  const haveAll = useHave();    // a post steps aside only once its Blender stone has landed
  const have = hostile ? {} : haveAll;   // (the hostile mini table never gets the glbs, so it never steps aside)
  return (
    <group>
      {parts.map((p) => {
        if (have.post && p.kind === "cyl" && p.tag === "post") return null;
        if (p.kind === "box" && !p.ghost && have["w_" + WALL_VARIANT[p.tag]]) return null;   // a wall steps aside once its Blender chunk has landed
        const mat = matFor(p.tag, p.ghost);
        const h = p.top - p.bottom;
        const yc = (p.top + p.bottom) / 2;
        if (p.kind === "cyl") {
          return (
            <mesh key={p.id} geometry={GEO.cyl} material={mat}
              position={[tx(p.x), yc, tz(p.y)]}
              scale={[p.r, h, p.r]}
              castShadow receiveShadow />
          );
        }
        return (
          <mesh key={p.id} geometry={GEO.box} material={mat}
            position={[tx(p.x), yc, tz(p.y)]}
            rotation={[0, p.rotY, 0]}
            scale={[p.len, h, p.thick]}
            castShadow receiveShadow />
        );
      })}
    </group>
  );
}

// ── Bumpers and captives get bodies, not boxes ────────────────
function Specials({ geo }) {
  const { specials, tx, tz } = geo;
  const t = useRef(0);
  const grp = useRef([]);

  useFrame((_, dt) => {
    t.current += dt;
    for (const m of grp.current) {
      if (!m) continue;
      m.material.emissiveIntensity = 0.5 + Math.sin(t.current * 2.4 + m.userData.ph) * 0.22;
    }
  });

  const cirque = geo.skin === "cirque";
  const have = useHave();       // a bumper steps aside only once its Blender tent has landed
  const tentTex   = useMemo(() => cirque ? stripeTexture(CIRQUE.red,  CIRQUE.cream, 16) : null, [cirque]);
  const drumTex   = useMemo(() => cirque ? stripeTexture(CIRQUE.plum, CIRQUE.cream, 12) : null, [cirque]);

  return (
    <group>
      {specials.map((s, i) => {
        if (have.bumper && s.tag === "bumper") return null;
        const col = colorOf(s.tag);
        if (s.tag === "captive") {
          return (
            <mesh key={s.id} geometry={GEO.sphere}
              position={[tx(s.x), s.base + s.r, tz(s.y)]}
              scale={[s.r, s.r, s.r]} castShadow>
              <meshStandardMaterial color={PF.bone} metalness={0.95} roughness={0.18} />
            </mesh>
          );
        }
        // A BUMPER IS A DRUM WITH A CAP THAT BURNS. The pop is the
        // best-feeling thing on any table; give it a body worth
        // hitting.
        //
        // ── AND AT THE CIRQUE, A BUMPER IS A TENT. ──────────────
        // Striped drum, scalloped crimson-and-cream canopy, brass
        // finial, and the lit cap becomes the ring of bulbs under
        // the canopy's brim. Same collider, same radius, same pop —
        // the ball cannot tell the difference, which is the law.
        const h = wallH("bumper");
        if (cirque) {
          return (
            <group key={s.id} position={[tx(s.x), s.base, tz(s.y)]}>
              {/* The drum — striped canvas wall. */}
              <mesh geometry={GEO.cyl} position={[0, (h - SKIRT) / 2, 0]}
                scale={[s.r, h + SKIRT, s.r]} castShadow receiveShadow>
                <meshStandardMaterial map={drumTex} metalness={0.15} roughness={0.6} />
              </mesh>
              {/* The bulb ring — this is the LIT part, driven by useFrame. */}
              <mesh
                ref={(m) => { if (m) { m.userData.ph = i * 1.7; grp.current[i] = m; } }}
                geometry={GEO.cyl} position={[0, h + 1.5, 0]}
                scale={[s.r * 1.16, 3.2, s.r * 1.16]} castShadow>
                <meshStandardMaterial
                  color={CIRQUE.bulb} emissive={CIRQUE.bulb} emissiveIntensity={0.6}
                  toneMapped={false} metalness={0.2} roughness={0.3} />
              </mesh>
              {/* The canopy — a striped tent-top with a brim past the drum. */}
              <mesh geometry={GEO.cone} position={[0, h + 3.2 + s.r * 0.55, 0]}
                scale={[s.r * 1.45, s.r * 1.1, s.r * 1.45]} castShadow>
                <meshStandardMaterial map={tentTex} metalness={0.1} roughness={0.55} />
              </mesh>
              {/* The brass finial. Every tent gets one. */}
              <mesh geometry={GEO.sphere}
                position={[0, h + 3.2 + s.r * 1.18, 0]}
                scale={[2.2, 2.2, 2.2]}>
                <meshStandardMaterial color={CIRQUE.brass} emissive={CIRQUE.brass}
                  emissiveIntensity={0.35} metalness={0.9} roughness={0.25} />
              </mesh>
            </group>
          );
        }
        return (
          <group key={s.id} position={[tx(s.x), s.base, tz(s.y)]}>
            <mesh geometry={GEO.cyl} position={[0, (h - SKIRT) / 2, 0]}
              scale={[s.r, h + SKIRT, s.r]} castShadow receiveShadow>
              <meshStandardMaterial color="#06202c" metalness={0.6} roughness={0.4} />
            </mesh>
            <mesh
              ref={(m) => { if (m) { m.userData.ph = i * 1.7; grp.current[i] = m; } }}
              geometry={GEO.cyl} position={[0, h + 1.5, 0]}
              scale={[s.r * 1.12, 4, s.r * 1.12]} castShadow>
              <meshStandardMaterial
                color={col} emissive={col} emissiveIntensity={0.6}
                toneMapped={false} metalness={0.2} roughness={0.3} />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
//  LAMPS — the language of the playfield
// ══════════════════════════════════════════════════════════════
// The engine already tells us what's lit (`table.lamps(engine)`),
// exactly as it told the 2D renderer. We just answer in light
// instead of in line width.
function Lamps({ geo, engineRef }) {
  const { lamps, tx, tz } = geo;
  const refs = useRef({});
  const t = useRef(0);

  useFrame((_, dt) => {
    t.current += dt;
    const e = engineRef.current;
    if (!e) return;
    const L = e.table.lamps ? e.table.lamps(e) : {};
    const lit = new Set([
      ...(L.targets || []), ...(L.nodes || []), ...(L.grudge || []),
    ]);
    const pulse = 0.5 + Math.sin(t.current * 5) * 0.5;

    for (const l of lamps) {
      const m = refs.current[l.id];
      if (!m) continue;
      const on = lit.has(l.id) || (l.tag === "crucible" && !!L.crucible);
      m.material.emissiveIntensity = on ? 0.9 + pulse * 1.0 : 0.16;
      if (l.solid) continue;            // a wall does not breathe. See buildParts.
      const s = on ? 1 + pulse * 0.14 : 0.85;
      if (l.cyl) m.scale.set(l.r * s, l.h, l.r * s);
      else       m.scale.set(l.len * s, l.h, l.thick);
      m.position.y = l.base + l.h / 2 + (on ? 1.2 + pulse * 0.8 : 0.4);
    }
  });

  return (
    <group>
      {lamps.map((l) => {
        const col = colorOf(l.tag);
        const bottom = l.buried ? l.base - SKIRT : l.base;
        const top = l.base + l.h;
        return (
          <mesh
            key={l.id}
            ref={(m) => { if (m) refs.current[l.id] = m; }}
            geometry={l.cyl ? GEO.cyl : GEO.box}
            position={[tx(l.x), (top + bottom) / 2, tz(l.y)]}
            rotation={[0, l.rotY, 0]}
            scale={l.cyl
              ? [l.r, top - bottom, l.r]
              : [Math.max(l.len, 6), top - bottom, l.thick]}
            castShadow receiveShadow
          >
            <meshStandardMaterial
              color={col} emissive={col} emissiveIntensity={0.16}
              toneMapped={false} metalness={0.35} roughness={0.32} />
          </mesh>
        );
      })}
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
//  DEVICES — a hole must look like the table simply STOPS
// ══════════════════════════════════════════════════════════════
function Devices({ geo }) {
  const { devices, tx, tz } = geo;
  const spin = useRef([]);
  const t = useRef(0);

  useFrame((_, dt) => {
    t.current += dt;
    spin.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.z = t.current * (m.userData.rate || 1);
      m.material.opacity = 0.35 + Math.sin(t.current * 3 + i) * 0.15;
    });
  });

  return (
    <group>
      {devices.map((d, i) => {
        const col = colorOf(d.tag);
        const deep = WELL_D[d.tag] ?? -10;
        return (
          <group key={d.id} position={[tx(d.x), 0, tz(d.y)]}>
            {/* The lip — a hot ring, so the mouth is visible in the dark. */}
            <mesh geometry={GEO.ring}
              position={[0, d.base + 0.9, 0]} rotation={[-Math.PI / 2, 0, 0]}
              scale={[d.r * 1.18, d.r * 1.18, 1]}>
              <meshBasicMaterial color={col} transparent opacity={0.9}
                toneMapped={false} side={THREE.DoubleSide} />
            </mesh>
            {/* The black. It is the only thing darker than the deck, and
                that is what makes it read as DEPTH rather than a decal. */}
            <mesh geometry={GEO.disc}
              position={[0, deep - 3, 0]} rotation={[-Math.PI / 2, 0, 0]}
              scale={[d.r * 0.95, d.r * 0.95, 1]}>
              <meshBasicMaterial color="#000000" />
            </mesh>
            {/* A whirlpool PULLS. Show the pull. */}
            {d.tag === "pool" && (
              <mesh
                ref={(m) => { if (m) { m.userData.rate = 1.6; spin.current[i] = m; } }}
                geometry={GEO.ring}
                position={[0, d.base - 3, 0]} rotation={[-Math.PI / 2, 0, 0]}
                scale={[d.r * 0.7, d.r * 0.7, 1]}>
                <meshBasicMaterial color={PF.violet} transparent opacity={0.4}
                  toneMapped={false} side={THREE.DoubleSide} />
              </mesh>
            )}
            <pointLight position={[0, d.base + 8, 0]} intensity={0.35}
              distance={70} decay={1.6} color={col} />
          </group>
        );
      })}
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
//  MOVERS — turnstiles and carts. THE LIE-PREVENTION LAYER.
// ══════════════════════════════════════════════════════════════
// Playfield.js learned this the hard way and wrote it down: bake a
// turnstile and you paint it once, at t=0, forever — so the ball
// gets batted by an invisible arm somewhere else and the player
// concludes the game is broken. They're not wrong. It is.
//
// Same law, new renderer. These read their endpoints EVERY FRAME.
function Movers({ geo, engineRef }) {
  const { matFor, tx, tz, heightAt } = geo;
  const refs = useRef({});

  const movers = useMemo(() => {
    const e = engineRef.current;
    if (!e) return [];
    return e.world.colliders.filter((c) => MOVING_TAGS.has(c.tag));
  }, [engineRef, geo]);

  useFrame(() => {
    const e = engineRef.current;
    if (!e) return;
    for (const c of e.world.colliders) {
      if (!MOVING_TAGS.has(c.tag)) continue;
      const m = refs.current[c.id];
      if (!m || !c.a || !c.b) continue;
      const mx = (c.a.x + c.b.x) / 2, my = (c.a.y + c.b.y) / 2;
      const L = Math.hypot(c.b.x - c.a.x, c.b.y - c.a.y) || 1;
      const base = heightAt(mx, my);
      const h = wallH(c.tag);
      m.position.set(tx(mx), base + h / 2, tz(my));
      m.rotation.y = Math.atan2(-(c.b.y - c.a.y), c.b.x - c.a.x);
      m.scale.set(L, h, c.thickness || 6);
    }
  });

  return (
    <group>
      {movers.map((c) => (
        <mesh key={c.id}
          ref={(m) => { if (m) refs.current[c.id] = m; }}
          geometry={GEO.box} material={matFor(c.tag)}
          castShadow receiveShadow />
      ))}
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
//  LAYER 5 — ACTORS. The flippers and the ball.
// ══════════════════════════════════════════════════════════════
function Flippers({ geo, engineRef, hostile }) {
  const { tx, tz, heightAt } = geo;
  const haveAll = useHave();    // the code-drawn bat steps aside only once its Blender bat has landed
  const have = hostile ? {} : haveAll;
  const bats = useRef({});
  const flippers = useMemo(() => {
    const e = engineRef.current;
    return e ? e.flippers.map((f) => ({ id: f.id, r: f.radius })) : [];
  }, [engineRef, geo]);

  useFrame(() => {
    const e = engineRef.current;
    if (!e) return;
    for (const f of e.flippers) {
      const m = bats.current[f.id];
      if (!m) continue;
      const mx = (f.pivot.x + f.tip.x) / 2, my = (f.pivot.y + f.tip.y) / 2;
      const L = Math.hypot(f.tip.x - f.pivot.x, f.tip.y - f.pivot.y) || 1;
      const base = heightAt(mx, my);
      m.position.set(tx(mx), base + f.radius + 1, tz(my));
      m.rotation.y = Math.atan2(-(f.tip.y - f.pivot.y), f.tip.x - f.pivot.x);
      m.scale.set(L + f.radius * 2, f.radius * 1.7, f.radius * 2);
      m.material.emissiveIntensity = f.pressed ? 1.5 : 0.35;
      m.material.color.set(f.pressed ? PF.flipperUp : PF.flipper);
    }
  });

  return (
    <group>
      {flippers.map((f) => (
        <mesh key={f.id}
          ref={(m) => { if (m) bats.current[f.id] = m; }}
          visible={!have.flipper}
          geometry={GEO.box} castShadow>
          <meshStandardMaterial
            color={PF.flipper} emissive={PF.flipper} emissiveIntensity={0.35}
            toneMapped={false} metalness={0.55} roughness={0.3} />
        </mesh>
      ))}
    </group>
  );
}

const MAX_BALLS = 6;
const TRAIL_N = 8;

function Balls({ geo, engineRef, hostile }) {
  const { tx, tz, heightAt } = geo;
  const balls = useRef([]);
  const trails = useRef([]);
  const lamp = useRef(null);

  useFrame(() => {
    const e = engineRef.current;
    if (!e) return;
    const live = e.world.balls.filter((b) => b.alive);

    for (let i = 0; i < MAX_BALLS; i++) {
      const m = balls.current[i];
      if (!m) continue;
      const b = live[i];
      if (!b) { m.visible = false; continue; }
      m.visible = true;

      const h = heightAt(b.p.x, b.p.y);
      m.position.set(tx(b.p.x), h + b.r, tz(b.p.y));
      m.scale.setScalar(b.r);

      // Freeze = the ball is glass. Overclock = the ball BURNS.
      // Ghost = the ball is barely there. Same language as 2D.
      const mat = m.material;
      if (b.frozen > 0) {
        mat.color.set(PF.ice); mat.emissive.set(PF.ice);
        mat.emissiveIntensity = 0.9; mat.metalness = 0.2; mat.roughness = 0.05;
      } else if (b.speedMul > 1) {
        mat.color.set("#ffd9c8"); mat.emissive.set(PF.ember);
        mat.emissiveIntensity = 1.3; mat.metalness = 0.9; mat.roughness = 0.14;
      } else {
        mat.color.set(PF.ball); mat.emissive.set(PF.ballShade);
        mat.emissiveIntensity = 0.22; mat.metalness = 0.98; mat.roughness = 0.12;
      }
      // ⚠ `transparent` is set ONCE, at creation, and only `opacity`
      //   moves. Flipping the transparent flag on a live material
      //   forces three to recompile the shader — every frame a ball
      //   ghosts in or out. Set it true forever; opacity 1 is opaque.
      mat.opacity = b.ghost > 0 ? 0.42 : 1;

      // The trail — the thing that makes 400 units/second LOOK like
      // 400 units/second.
      const tr = b.trail || [];
      for (let k = 0; k < TRAIL_N; k++) {
        const t = trails.current[i * TRAIL_N + k];
        if (!t) continue;
        const p = tr[tr.length - 1 - k];
        if (!p) { t.visible = false; continue; }
        t.visible = true;
        const f = 1 - k / TRAIL_N;
        t.position.set(tx(p.x), heightAt(p.x, p.y) + b.r * 0.85, tz(p.y));
        t.scale.setScalar(b.r * 0.62 * f);
        t.material.opacity = 0.30 * f;
      }
    }

    // The ball carries its own light. Not decoration — it is how you
    // find the ball at the bottom of a dark loop.
    if (lamp.current) {
      const b = live[0];
      if (b) {
        lamp.current.visible = true;
        lamp.current.position.set(tx(b.p.x), heightAt(b.p.x, b.p.y) + 26, tz(b.p.y));
      } else lamp.current.visible = false;
    }
  });

  return (
    <group>
      {Array.from({ length: MAX_BALLS }).map((_, i) => (
        <mesh key={`b${i}`} geometry={GEO.sphere} visible={false} castShadow
          ref={(m) => { if (m) balls.current[i] = m; }}>
          <meshStandardMaterial color={PF.ball} emissive={PF.ballShade}
            emissiveIntensity={0.22} metalness={0.98} roughness={0.12}
            transparent opacity={1} />
        </mesh>
      ))}
      {Array.from({ length: MAX_BALLS * TRAIL_N }).map((_, i) => (
        <mesh key={`t${i}`} geometry={GEO.sphere} visible={false}
          ref={(m) => { if (m) trails.current[i] = m; }}>
          <meshBasicMaterial color={PF.ball} transparent opacity={0.2} toneMapped={false} />
        </mesh>
      ))}
      {!hostile && (
        <pointLight ref={lamp} intensity={0.9} distance={190} decay={1.7}
          color="#cfe3ff" visible={false} />
      )}
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
//  POWERS — anchors, magnets, and the pop
// ══════════════════════════════════════════════════════════════
const MAX_TEMPS = 10;

function Temps({ geo, engineRef }) {
  const { tx, tz, heightAt } = geo;
  const bars = useRef([]);

  useFrame(() => {
    const e = engineRef.current;
    if (!e) return;
    for (let i = 0; i < MAX_TEMPS; i++) {
      const m = bars.current[i];
      if (!m) continue;
      const t = e.tempColliders[i];
      const c = t ? e.world.getCollider(t.id) : null;
      if (!c || !c.a) { m.visible = false; continue; }
      m.visible = true;
      const hostile = c.tag === "anchor_hostile";
      const col = hostile ? PF.ember : PF.gold;
      const mx = (c.a.x + c.b.x) / 2, my = (c.a.y + c.b.y) / 2;
      const L = Math.hypot(c.b.x - c.a.x, c.b.y - c.a.y) || 1;
      const base = heightAt(mx, my);
      const a = Math.min(1, t.ttl / 1.5);
      m.position.set(tx(mx), base + 9, tz(my));
      m.rotation.y = Math.atan2(-(c.b.y - c.a.y), c.b.x - c.a.x);
      m.scale.set(L, 18, c.thickness || 6);
      m.material.color.set(col);
      m.material.emissive.set(col);
      m.material.emissiveIntensity = 0.6 + a * 1.2;
      m.material.opacity = 0.45 + 0.55 * a;
    }
  });

  return (
    <group>
      {Array.from({ length: MAX_TEMPS }).map((_, i) => (
        <mesh key={i} geometry={GEO.box} visible={false} castShadow
          ref={(m) => { if (m) bars.current[i] = m; }}>
          <meshStandardMaterial color={PF.gold} emissive={PF.gold}
            emissiveIntensity={1} transparent opacity={0.9} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

const MAX_FIELDS = 4;

function Fields({ geo, engineRef }) {
  const { tx, tz, heightAt } = geo;
  const rings = useRef([]);
  const t = useRef(0);

  useFrame((_, dt) => {
    t.current += dt;
    const e = engineRef.current;
    if (!e) return;
    const fs = e.world.fields.filter((f) => f.kind === "magnet");
    for (let i = 0; i < MAX_FIELDS; i++) {
      const g = rings.current[i];
      if (!g) continue;
      const f = fs[i];
      if (!f) { g.visible = false; continue; }
      g.visible = true;
      const a = Math.min(1, (f.ttl ?? 1) / 1.2);
      const pr = f.radius * (0.35 + 0.65 * ((t.current * 1.6) % 1));
      g.position.set(tx(f.c.x), heightAt(f.c.x, f.c.y) + 2.5, tz(f.c.y));
      g.scale.set(pr, pr, 1);
      g.material.opacity = 0.55 * a * (1 - ((t.current * 1.6) % 1));
    }
  });

  return (
    <group>
      {Array.from({ length: MAX_FIELDS }).map((_, i) => (
        <mesh key={i} geometry={GEO.ring} visible={false}
          rotation={[-Math.PI / 2, 0, 0]}
          ref={(m) => { if (m) rings.current[i] = m; }}>
          <meshBasicMaterial color={PF.gold} transparent opacity={0.5}
            toneMapped={false} side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
}

// ── THE POP ───────────────────────────────────────────────────
// The engine has been pushing `{ fx: "pop", at, pts }` into a
// buffer since day one and NOBODY HAS EVER DRAWN IT. Nothing
// consumed it; the 2D renderer never looked. It has been sitting
// there, correct, unread. So we read it — non-destructively, with
// a key cursor, because `drainFx()` is a shared resource and a
// renderer does not get to empty a queue somebody else may want.
const MAX_POPS = 14;

function Pops({ geo, engineRef }) {
  const { tx, tz, heightAt } = geo;
  const meshes = useRef([]);
  const live = useRef([]);
  const cursor = useRef(0);
  const next = useRef(0);

  useFrame((_, dt) => {
    const e = engineRef.current;
    if (!e) return;

    for (const f of e.fx) {
      if (f.key <= cursor.current) continue;
      cursor.current = f.key;
      if (!f.at) continue;
      const kinds = ["pop", "jackpot", "freeze", "split", "ghost", "reverse", "magnet"];
      if (!kinds.includes(f.fx)) continue;
      const i = next.current % MAX_POPS;
      next.current++;
      live.current[i] = {
        x: f.at.x, y: f.at.y, t: 0,
        big: f.fx === "jackpot" ? 2.2 : 1,
        col: f.fx === "jackpot" ? PF.gold
          : f.fx === "freeze" ? PF.ice
          : f.fx === "ghost" ? PF.violet
          : f.fx === "magnet" ? PF.gold
          : PF.neon,
      };
    }

    for (let i = 0; i < MAX_POPS; i++) {
      const m = meshes.current[i];
      const p = live.current[i];
      if (!m) continue;
      if (!p) { m.visible = false; continue; }
      p.t += dt * 2.6;
      if (p.t >= 1) { live.current[i] = null; m.visible = false; continue; }
      m.visible = true;
      const s = (10 + p.t * 42) * p.big;
      m.position.set(tx(p.x), heightAt(p.x, p.y) + 4 + p.t * 12, tz(p.y));
      m.scale.set(s, s, 1);
      m.material.color.set(p.col);
      m.material.opacity = (1 - p.t) * 0.85;
    }
  });

  return (
    <group>
      {Array.from({ length: MAX_POPS }).map((_, i) => (
        <mesh key={i} geometry={GEO.ring} visible={false}
          rotation={[-Math.PI / 2, 0, 0]}
          ref={(m) => { if (m) meshes.current[i] = m; }}>
          <meshBasicMaterial color={PF.neon} transparent opacity={0.8}
            toneMapped={false} side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
}

// ── The aim reticle — where the power will land ────────────────
function Reticle({ geo, engineRef, aiming }) {
  const { tx, tz, heightAt } = geo;
  const ring = useRef(null);
  const beam = useRef(null);
  const t = useRef(0);

  useFrame((_, dt) => {
    t.current += dt;
    const e = engineRef.current;
    const h = aiming && e ? e._aimHover : null;
    const vis = !!h;
    if (ring.current) ring.current.visible = vis;
    if (beam.current) beam.current.visible = vis;
    if (!h) return;
    const col = tintOf(String(aiming).includes("anchor") ? "gold" : "violet");
    const base = heightAt(h.x, h.y);
    const s = 17 + Math.sin(t.current * 6) * 3;
    if (ring.current) {
      ring.current.position.set(tx(h.x), base + 2.5, tz(h.y));
      ring.current.scale.set(s, s, 1);
      ring.current.material.color.set(col);
    }
    if (beam.current) {
      beam.current.position.set(tx(h.x), base + 30, tz(h.y));
      beam.current.scale.set(1.4, 60, 1.4);
      beam.current.material.color.set(col);
      beam.current.material.opacity = 0.25 + Math.sin(t.current * 6) * 0.12;
    }
  });

  return (
    <group>
      <mesh ref={ring} geometry={GEO.ring} visible={false}
        rotation={[-Math.PI / 2, 0, 0]}>
        <meshBasicMaterial color={PF.gold} transparent opacity={0.9}
          toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh ref={beam} geometry={GEO.cyl} visible={false}>
        <meshBasicMaterial color={PF.gold} transparent opacity={0.3}
          toneMapped={false} />
      </mesh>
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
//  THE DRESSING LAYER — the circus, hung over the machine
// ══════════════════════════════════════════════════════════════
// Everything in here is pure theatre: canopies over the landmarks,
// bunting strung across the top of the cabinet, two spotlight
// beams from the marquee. None of it exists at ball height, none
// of it casts a collider, and all of it is derived — the tents
// stand where the LANDMARKS said the rides are.
//
// ⚠ THE ONE RULE OF DRESSING: NOTHING BETWEEN THE LENS AND THE
//   BALL. Every piece here either stands ABOVE the tallest wall
//   (bunting, beams) or covers deck the ball cannot occupy (the
//   dead hub inside a loop, the centre of the prize ring). A
//   canopy wide enough to shade the track would hide the ball at
//   the exact moment it's doing the most interesting thing on the
//   table. So canopies are narrow, tall, and centred on dead space.
function CirqueDressing({ geo, hostile }) {
  const { skin, landmarks, W, H, maxH, tx, tz } = geo;
  const flags = useRef([]);
  const strandMats = useRef([]);
  const t = useRef(0);

  const tentTex = useMemo(
    () => (skin === "cirque" ? stripeTexture(CIRQUE.red, CIRQUE.cream, 14) : null),
    [skin]
  );
  const poleTex = useMemo(
    () => (skin === "cirque" ? stripeTexture(CIRQUE.red, CIRQUE.cream, 8) : null),
    [skin]
  );

  // ── The bunting — three sagging strands of pennants and bulbs.
  // A catenary is overkill; a parabola reads identically from ten
  // feet away, and this camera is never closer than that.
  const strands = useMemo(() => {
    if (skin !== "cirque") return [];
    const yTop = maxH + 58;
    const rows = [-0.34, -0.13, 0.10];
    return rows.map((fz, r) => {
      const z = H * fz;
      const n = 13;
      const pts = [];
      for (let i = 0; i < n; i++) {
        const f = i / (n - 1);
        const sag = Math.sin(f * Math.PI) * 16;
        pts.push({
          x: -W / 2 + f * W,
          y: yTop - (r % 2) * 9 - sag,
          z,
          bulb: i % 2 === 0,
        });
      }
      return { pts, z, r };
    });
  }, [skin, W, H, maxH]);

  useFrame((_, dt) => {
    t.current += dt;
    // The flags wave. Slowly. A flag that whips reads as WIND, and
    // there is no wind indoors — there's a draught.
    flags.current.forEach((m, i) => {
      if (m) m.rotation.y = Math.sin(t.current * 1.4 + i * 2.1) * 0.35;
    });
    // The strands twinkle as whole strands — cheap, and it reads.
    strandMats.current.forEach((arr, i) => {
      const o = 0.72 + Math.sin(t.current * 2.2 + i * 2.4) * 0.22;
      if (arr) for (const m of arr) m.opacity = o;
    });
  });

  if (skin !== "cirque" || hostile) return null;

  return (
    <group>
      {/* ── The canopies — one striped big-top per landmark. ───── */}
      {landmarks.tents.map((tt, i) => {
        const coneH = 26 + tt.r * 0.5;
        const poleH = coneH + 26;
        return (
          <group key={`tent${i}`} position={[tx(tt.x), tt.base, tz(tt.y)]}>
            {/* The pole — candy-striped, from the bed to the peak. */}
            <mesh geometry={GEO.cyl} position={[0, poleH / 2, 0]}
              scale={[2.4, poleH, 2.4]} castShadow>
              <meshStandardMaterial map={poleTex} metalness={0.15} roughness={0.5} />
            </mesh>
            {/* The canopy — narrow. It covers the DEAD HUB, not the track. */}
            <mesh geometry={GEO.cone} position={[0, poleH - coneH * 0.4, 0]}
              scale={[tt.r * 0.72, coneH, tt.r * 0.72]} castShadow>
              <meshStandardMaterial map={tentTex} metalness={0.1} roughness={0.55}
                side={THREE.DoubleSide} />
            </mesh>
            {/* The pennant. It waves. */}
            <mesh geometry={GEO.box}
              ref={(m) => { if (m) flags.current[i] = m; }}
              position={[5.5, poleH + 6, 0]} scale={[11, 5, 0.4]}>
              <meshStandardMaterial color={CIRQUE.brass} emissive={CIRQUE.brass}
                emissiveIntensity={0.5} toneMapped={false} side={THREE.DoubleSide} />
            </mesh>
            {/* Warm lamp under the brim. The tent GLOWS from inside. */}
            <pointLight position={[0, poleH - coneH * 0.7, 0]} intensity={0.4}
              distance={tt.r * 3.4} decay={1.8} color={CIRQUE.bulb} />
          </group>
        );
      })}

      {/* ── The bunting — pennants and bulbs on sagging strands. ── */}
      {strands.map((st, si) => (
        <group key={`strand${si}`}>
          {st.pts.map((p, i) =>
            p.bulb ? (
              <mesh key={`b${i}`} geometry={GEO.sphere}
                position={[p.x, p.y, p.z]} scale={[2.1, 2.1, 2.1]}>
                <meshBasicMaterial color={CIRQUE.bulb} toneMapped={false}
                  transparent opacity={0.9}
                  ref={(m) => {
                    if (!m) return;
                    (strandMats.current[si] ||= []).push(m);
                  }} />
              </mesh>
            ) : (
              <mesh key={`p${i}`} geometry={GEO.cone}
                position={[p.x, p.y - 4.5, p.z]}
                rotation={[Math.PI, 0, 0]}
                scale={[3.4, 8, 0.5]}>
                <meshStandardMaterial
                  color={i % 4 === 1 ? CIRQUE.red : CIRQUE.cream}
                  emissive={i % 4 === 1 ? CIRQUE.red : CIRQUE.cream}
                  emissiveIntensity={0.22} side={THREE.DoubleSide} />
              </mesh>
            )
          )}
        </group>
      ))}

      {/* ── The spotlights — two faint beams from the marquee. ──── */}
      {[-0.22, 0.22].map((fx, i) => (
        <mesh key={`beam${i}`} geometry={GEO.cone}
          position={[W * fx, maxH + 55, -H * 0.18]}
          rotation={[0.5, 0, fx > 0 ? -0.28 : 0.28]}
          scale={[26, 130, 26]}>
          <meshBasicMaterial color={CIRQUE.bulb} transparent opacity={0.05}
            toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
//  THE SCENE
// ══════════════════════════════════════════════════════════════
function Scene({ geo, engineRef, mode, hostile, aiming, onAim }) {
  return (
    <>
      <color attach="background" args={["#03040a"]} />
      <fog attach="fog" args={["#03040a", 900, 2400]} />
      <Lights geo={geo} hostile={hostile} />
      <CameraRig geo={geo} mode={mode} engineRef={engineRef} hostile={hostile} />
      <Cabinet geo={geo} hostile={hostile} />
      <CirqueDressing geo={geo} hostile={hostile} />
      <Deck geo={geo} aiming={aiming} onAim={onAim} engineRef={engineRef} />
      <SetPieces geo={geo} engineRef={engineRef} hostile={hostile} />
      <Statics geo={geo} hostile={hostile} />
      <Specials geo={geo} />
      <Lamps geo={geo} engineRef={engineRef} />
      <Devices geo={geo} />
      <Movers geo={geo} engineRef={engineRef} />
      <Flippers geo={geo} engineRef={engineRef} hostile={hostile} />
      <Balls geo={geo} engineRef={engineRef} hostile={hostile} />
      <Temps geo={geo} engineRef={engineRef} />
      <Fields geo={geo} engineRef={engineRef} />
      <Pops geo={geo} engineRef={engineRef} />
      <Reticle geo={geo} engineRef={engineRef} aiming={aiming} />
      {mode === "free" && (
        <OrbitControls
          enableDamping dampingFactor={0.08}
          minDistance={120} maxDistance={2600}
          maxPolarAngle={Math.PI / 2.06}   // never orbit under the deck
        />
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════
//  THE COMPONENT — same props as Playfield.js. Non-negotiable.
// ══════════════════════════════════════════════════════════════
/**
 * @param engineRef  a useRef holding the live TiltEngine
 * @param snap       the latest snapshot (drives re-render of overlays)
 * @param onAim      (tablePoint) => void — a click while aiming
 * @param aiming     the power id currently being aimed (or null)
 * @param hostile    render tint for an opponent's table in a duel
 */
export default function Playfield3D({
  engineRef, snap, onAim, aiming, hostile = false, scale: forcedScale,
}) {
  const [mode, setMode] = useState("play");
  const eng = engineRef.current;

  // The whole table, built once. An opponent's table in a duel is a
  // SECOND WebGL context on the same screen, so it gets fewer wall
  // chunks, no shadows, and no ball lamp. It's 260px wide. Nobody
  // has ever squinted at the bevel on a rail they're sabotaging.
  const geo = useMemo(
    () => (eng ? buildTableGeo(eng, hostile) : null),
    [eng, hostile]
  );
  useEffect(() => () => geo?.dispose?.(), [geo]);

  const tilted = snap?.phase === "tilted";
  const live   = !!hostile || mode !== "free";

  // ── THE FRAME. It IS the bay now. ─────────────────────────────
  // 2026-07-14: the aspect-locked frame is retired. It centred a
  // shot-shaped box in the bay and left SoloScreen's grey showing on
  // the flanks — the exact dead space caP'n asked to delete. The rig
  // already re-solves the camera against the LIVE canvas aspect
  // (CameraRig reads `size`), so the frame can simply take every
  // pixel the screen hands it: the table locks edge-to-edge top and
  // bottom, and whatever width is left over shows SCENE — cabinet,
  // dark, and eventually the flank animations — never chrome grey.
  // shotAspect() stays in the file for reference but has no caller.
  const aspect = null;
  void shotAspect;

  const EDGE = hostile ? "rgba(240,64,122,0.34)" : "rgba(52,217,232,0.30)";

  return (
    // ── THE WELL ────────────────────────────────────────────────
    // Whatever box the screen hands us, we take ALL of it — and
    // then centre a correctly-shaped frame inside. The screen owns
    // the space; the renderer owns the picture. Neither one has to
    // know the other's numbers, which is what lets SoloScreen give
    // this thing a widescreen bay and DuelScreen give it a slot.
    <div style={{
      position: "relative",
      width: "100%", height: "100%", minWidth: 0, minHeight: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        // ⚠ STILL HEIGHT-FIRST. The window is short and the box is
        //   the binding constraint — that hasn't changed and never
        //   will. What changed is the SHAPE we ask for: the shot's,
        //   not the table's. See shotAspect().
        position: "relative",
        height: "100%",
        width: aspect ? "auto" : "100%",
        maxWidth: "100%",
        maxHeight: "100%",
        ...(aspect ? { aspectRatio: `${aspect}` } : null),
        borderRadius: 12,
        overflow: "hidden",
        cursor: aiming ? "crosshair" : "default",
        touchAction: "none",
        background: "#03040a",
        boxShadow: [
          `inset 0 0 0 1px ${EDGE}`,                      // the bezel's inner rim
          "inset 0 1px 0 rgba(255,255,255,0.05)",         // a light source, always (LAW 3)
          "0 24px 70px rgba(0,0,0,0.9)",                  // the machine has WEIGHT
          hostile
            ? "0 0 40px rgba(240,64,122,0.10)"
            : "0 0 60px rgba(52,217,232,0.10)",           // and it HUMS
        ].join(", "),
      }}>
        {geo && (
          <Canvas
            shadows={!hostile}
            dpr={[1, hostile ? 1.25 : 2]}
            gl={{ antialias: true, powerPreference: "high-performance" }}
            camera={{ fov: FOV, near: 5, far: 6000, position: [0, 600, 700] }}
          >
            <Scene
              geo={geo}
              engineRef={engineRef}
              mode={hostile ? "play" : mode}
              hostile={hostile}
              aiming={aiming}
              onAim={onAim}
            />
          </Canvas>
        )}

        {/* ── THE GLASS ────────────────────────────────────────
            A pinball table is under a sheet of plate glass, and the
            three cheapest lies in this file are the ones that sell
            it: a vignette so the cabinet swallows its own corners,
            a raking sheen so there's something BETWEEN you and the
            deck, and a hairline of scan. All three are
            pointerEvents:none — the aim raycast goes straight
            through them, because the glass is not a click target.
            (If you ever make one of these clickable, aim breaks and
            you will spend an hour blaming the raycaster.) */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background:
            "radial-gradient(120% 90% at 50% 42%, transparent 46%, rgba(0,0,0,0.62) 100%)",
        }} />
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background:
            "linear-gradient(158deg, rgba(228,235,247,0.055) 0%, rgba(228,235,247,0.015) 22%, transparent 42%)",
        }} />
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.55,
          background:
            "repeating-linear-gradient(0deg, transparent 0 3px, rgba(255,255,255,0.018) 3px 4px)",
        }} />

        {/* Corner brackets — machined, not rounded. The frame is a
            fabricated object and it should look bolted together. */}
        {!hostile && [
          { top: 7, left: 7, borderTop: 1, borderLeft: 1 },
          { top: 7, right: 7, borderTop: 1, borderRight: 1 },
          { bottom: 7, left: 7, borderBottom: 1, borderLeft: 1 },
          { bottom: 7, right: 7, borderBottom: 1, borderRight: 1 },
        ].map((c, i) => (
          <div key={i} style={{
            position: "absolute", width: 16, height: 16, pointerEvents: "none",
            top: c.top, left: c.left, right: c.right, bottom: c.bottom,
            borderTop:    c.borderTop    ? `1px solid ${PF.rail}` : "none",
            borderLeft:   c.borderLeft   ? `1px solid ${PF.rail}` : "none",
            borderRight:  c.borderRight  ? `1px solid ${PF.rail}` : "none",
            borderBottom: c.borderBottom ? `1px solid ${PF.rail}` : "none",
            opacity: 0.4,
          }} />
        ))}

        {/* ── Hostile tint — an opponent's table reads COLD ───── */}
        {hostile && (
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: "rgba(160,180,255,0.16)", mixBlendMode: "multiply",
          }} />
        )}

        {/* ── Tilt — the table is dying and you can see it ────── */}
        {tilted && (
          <div style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: "rgba(240,80,107,0.12)",
          }} />
        )}

        {/* ── THE LENS POD. The only DOM in this file, and the only
               thing in it allowed to be UI chrome. ───────────── */}
        {!hostile && (
          <div style={{
            position: "absolute", top: 9, right: 9,
            display: "flex", gap: 3, padding: 3,
            background: "rgba(4,5,10,0.74)",
            border: "1px solid rgba(125,143,196,0.22)",
            borderRadius: 5,
            backdropFilter: "blur(4px)",
            pointerEvents: "auto",
          }}>
            {[
              ["play", "PLAY"],
              ["top",  "TOP"],
              ["free", "FREE"],
            ].map(([m, label]) => {
              const on = mode === m;
              return (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  style={{
                    background: on ? "rgba(34,211,238,0.20)" : "transparent",
                    border: `1px solid ${on ? PF.neon : "transparent"}`,
                    color: on ? PF.neon : PF.rail,
                    font: `700 9px ${PF.fontPF}`,
                    letterSpacing: 1.5,
                    padding: "4px 8px",
                    borderRadius: 3,
                    cursor: "pointer",
                    userSelect: "none",
                    textShadow: on ? `0 0 8px ${PF.neon}aa` : "none",
                    transition: "background 120ms, color 120ms",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* FREE is a different activity. Say so, once, quietly. */}
        {!hostile && !live && (
          <div style={{
            position: "absolute", left: 12, bottom: 10, pointerEvents: "none",
            font: `600 9px ${PF.fontPF}`, letterSpacing: 1.4,
            color: PF.rail, opacity: 0.75,
          }}>
            DRAG TO ORBIT · SCROLL TO ZOOM
          </div>
        )}
      </div>
    </div>
  );
}
